import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { 
  FileText, AlertCircle, ChevronLeft, ChevronRight, ChevronDown,
  ZoomIn, ZoomOut, Check, X, Move, User, UserPlus, 
  Mail, Settings, Type, Phone, Calendar, Hash, Maximize2, RefreshCw,
  MapPin, DollarSign, Pen, Edit3
} from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../utils/apiConfig';
import { documentService } from '../services/documentService';
import { contactService } from '../services/contactService';
// import { formFieldService } from '../services/formFieldService';
import { useAuth } from '../contexts/AuthContext';
import ContactSelector from './ContactSelector';
import SignaturePlacement from './SignaturePlacement';

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Apple-inspired design system matching DocumentViewer.jsx
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
  success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)',
  successLight: 'rgba(52, 199, 89, 0.1)',
  warningLight: 'rgba(255, 149, 0, 0.1)',
  errorLight: 'rgba(255, 59, 48, 0.1)',
};

const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

/**
 * Form field types configuration
 */
export const FORM_FIELD_TYPES = {
  name: { label: 'Name', icon: User, placeholder: 'Enter full name', validation: { maxLength: 100 } },
  title: { label: 'Title', icon: Hash, placeholder: 'Enter job title', validation: { maxLength: 50 } },
  email: { label: 'Email', icon: Mail, placeholder: 'Enter email address', validation: { pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ } },
  phone: { label: 'Phone', icon: Phone, placeholder: 'Enter phone number', validation: { pattern: /^\+?[1-9]\d{0,15}$/ } },
  date: { label: 'Date', icon: Calendar, placeholder: 'MM/DD/YYYY', validation: { pattern: /^\d{2}\/\d{2}\/\d{4}$/ } },
  address: { label: 'Address', icon: MapPin, placeholder: 'Enter address', validation: { maxLength: 200 } },
  amount: { label: 'Amount', icon: DollarSign, placeholder: 'Enter amount', validation: { pattern: /^\$?[\d,]+\.?\d*$/ } },
  text: { label: 'Text', icon: Type, placeholder: 'Enter text', validation: { maxLength: 200 } }
};

/**
 * SignatureFieldPlacement - Component for placing signature fields and form fields on documents and assigning them to contacts
 */
const SignatureFieldPlacement = ({ 
  documentId, 
  onComplete, 
  onCancel,
  embedded = false, // New prop to indicate if this is embedded in NewDocument
  onSignatureAdded = null, // Callback for when a signature is added
  onFieldClick = null // Callback for when a field is clicked
}) => {
  const { user } = useAuth();
  
  // Document state
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [autoScale, setAutoScale] = useState(true);
  const [documentData, setDocumentData] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfDocument, setPdfDocument] = useState(null);
  
  // Signature field state
  const [signatureFields, setSignatureFields] = useState([]);
  const [activeField, setActiveField] = useState(null);
  const [hoveredField, setHoveredField] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStartSize, setResizeStartSize] = useState({ width: 0, height: 0 });
  const [hasDraggedSignificantly, setHasDraggedSignificantly] = useState(false);
  
  // Form field state
  const [formFields, setFormFields] = useState([]);
  const [selectedFormFieldType, setSelectedFormFieldType] = useState('name');
  const [currentFieldType, setCurrentFieldType] = useState('signature'); // 'signature' or 'form'
  
  // Contact slot display state
  const [selectedContactSlot, setSelectedContactSlot] = useState('manual'); // Track which contact slot is currently displayed in dropdown ('manual' or 1-3)
  const [activeContactSlot, setActiveContactSlot] = useState('manual'); // Track which contact slot is active for auto-assignment ('manual' or 1-9)
  
  // Helper function to get the currently selected contact for auto-assignment
  const getCurrentlySelectedContact = () => {
    if (activeContactSlot === 'manual') {
      return null; // No auto-assignment in manual mode
    }
    
    if (activeContactSlot === 1) {
      // Return current user
      return {
        id: 'current-user',
        name: user?.name || 'Me',
        email: user?.email || '',
        isCurrentUser: true
      };
    }
    
    // For slots 2-9, get the contact from the contacts array
    const availableContacts = contacts.filter(c => !c.isCurrentUser);
    const contactIndex = activeContactSlot - 2; // Convert to 0-based index
    return availableContacts[contactIndex] || null;
  };
  
  // Contact assignment state
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedFieldForAssignment, setSelectedFieldForAssignment] = useState(null);
  const [contacts, setContacts] = useState([
    // Start with current user as default contact
    {
      id: 'current-user',
      name: user?.name || 'Me',
      email: user?.email || '',
      isCurrentUser: true
    }
  ]);
  
  // Signature state for immediate signing
  const [signedFields, setSignedFields] = useState(new Set());
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [fieldToSign, setFieldToSign] = useState(null);
  
  // Signature placement state (for when user needs to create signature)
  const [showSignaturePlacement, setShowSignaturePlacement] = useState(false);
  const [selectedSignatureField, setSelectedSignatureField] = useState(null);
  const [newContactName, setNewContactName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [userContacts, setUserContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Keyboard shortcuts state
  const [showShortcutHint, setShowShortcutHint] = useState(true);
  const [creatingContact, setCreatingContact] = useState(false);
  const [contactCreationError, setContactCreationError] = useState('');
  const [showGrid, setShowGrid] = useState(false);
  
  // Collision feedback state
  const [collisionWarning, setCollisionWarning] = useState(false);
  const [snapGuides, setSnapGuides] = useState({ x: [], y: [] });


  // Utility function to check rectangle overlap (axis-aligned bounding boxes)
  const rectanglesOverlap = (a, b) => {
    return !(a.x + a.width <= b.x ||
             b.x + b.width <= a.x ||
             a.y + a.height <= b.y ||
             b.y + b.height <= a.y);
  };

  // Check if a candidate rectangle overlaps any other field on the same page
  const isOverlappingOtherFields = useCallback((candidate, page, ignoreId = null) => {
    const allFields = [...signatureFields, ...formFields];
    for (const f of allFields) {
      if (f.id === ignoreId || f.page !== page) continue;
      const otherRect = {
        x: f.x,
        y: f.y,
        width: f.width || (f.type === 'signature' ? 200 : 150),
        height: (f.height || (f.type === 'signature' ? 70 : 50)) + (f.type === 'signature' ? 25 : 0) // Add 25px for signature ID bar
      };
      if (rectanglesOverlap(candidate, otherRect)) {
        return true;
      }
    }
    return false;
  }, [signatureFields, formFields]);

  // Find a non-overlapping position for a new field
  const findNonOverlappingPosition = useCallback((originalRect, page) => {
    let candidate = { ...originalRect };
    const step = 2; // Move in 2px increments for tight spacing
    const maxAttempts = 75; // Balanced attempts for close positions
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (!isOverlappingOtherFields(candidate, page)) {
        return candidate;
      }
      
      // Try moving right first
      candidate.x += step;
      if (!isOverlappingOtherFields(candidate, page)) {
        return candidate;
      }
      
      // If right doesn't work, try down
      candidate.x = originalRect.x;
      candidate.y += step * (attempt + 1);
      if (!isOverlappingOtherFields(candidate, page)) {
        return candidate;
      }
      
      // Try diagonal (right and down)
      candidate.x = originalRect.x + step * (attempt + 1);
      if (!isOverlappingOtherFields(candidate, page)) {
        return candidate;
      }
      
      // Reset and try a different pattern
      candidate = { ...originalRect };
      candidate.x -= step * (attempt + 1);
      candidate.y += step * (attempt + 1);
      if (candidate.x >= 0 && !isOverlappingOtherFields(candidate, page)) {
        return candidate;
      }
    }
    
    // If all else fails, return original position
    return originalRect;
  }, [isOverlappingOtherFields]);

  // Get allowed movement directions based on collision detection
  const getAllowedMovement = useCallback((field, newX, newY) => {
    const fieldWidth = field.width || (field.type === 'signature' ? 200 : 150);
    const fieldHeight = field.height || (field.type === 'signature' ? 70 : 50);
    
    // Test movement in each direction
    const canMoveX = !isOverlappingOtherFields({
      x: newX,
      y: field.y,
      width: fieldWidth,
      height: fieldHeight + (field.type === 'signature' ? 25 : 0) // Add 25px for signature ID bar
    }, field.page, field.id);
    
    const canMoveY = !isOverlappingOtherFields({
      x: field.x,
      y: newY,
      width: fieldWidth,
      height: fieldHeight + (field.type === 'signature' ? 25 : 0) // Add 25px for signature ID bar
    }, field.page, field.id);
    
    return { canMoveX, canMoveY };
  }, [isOverlappingOtherFields]);

  // Snap field to edges of other fields when close
  const getSnappedPosition = useCallback((field, targetX, targetY) => {
    const fieldWidth = field.width || (field.type === 'signature' ? 200 : 150);
    const fieldHeight = field.height || (field.type === 'signature' ? 70 : 50);
    const snapDistance = 10; // Snap when within 10px
    
    let snappedX = targetX;
    let snappedY = targetY;
    const snapGuidesX = [];
    const snapGuidesY = [];
    
    const allFields = [...signatureFields, ...formFields];
    
    for (const otherField of allFields) {
      if (otherField.id === field.id || otherField.page !== field.page) continue;
      
      const otherWidth = otherField.width || (otherField.type === 'signature' ? 200 : 150);
      const otherHeight = otherField.height || (otherField.type === 'signature' ? 70 : 50);
      
      // Check for horizontal snapping
      // Snap left edge to right edge of other field
      if (Math.abs(targetX - (otherField.x + otherWidth)) <= snapDistance) {
        snappedX = otherField.x + otherWidth;
        snapGuidesX.push(snappedX);
      }
      // Snap right edge to left edge of other field
      else if (Math.abs((targetX + fieldWidth) - otherField.x) <= snapDistance) {
        snappedX = otherField.x - fieldWidth;
        snapGuidesX.push(otherField.x);
      }
      // Snap left edges to align
      else if (Math.abs(targetX - otherField.x) <= snapDistance) {
        snappedX = otherField.x;
        snapGuidesX.push(snappedX);
      }
      // Snap right edges to align
      else if (Math.abs((targetX + fieldWidth) - (otherField.x + otherWidth)) <= snapDistance) {
        snappedX = otherField.x + otherWidth - fieldWidth;
        snapGuidesX.push(otherField.x + otherWidth);
      }
      
      // Check for vertical snapping
      // Snap top edge to bottom edge of other field
      if (Math.abs(targetY - (otherField.y + otherHeight)) <= snapDistance) {
        snappedY = otherField.y + otherHeight;
        snapGuidesY.push(snappedY);
      }
      // Snap bottom edge to top edge of other field
      else if (Math.abs((targetY + fieldHeight) - otherField.y) <= snapDistance) {
        snappedY = otherField.y - fieldHeight;
        snapGuidesY.push(otherField.y);
      }
      // Snap top edges to align
      else if (Math.abs(targetY - otherField.y) <= snapDistance) {
        snappedY = otherField.y;
        snapGuidesY.push(snappedY);
      }
      // Snap bottom edges to align
      else if (Math.abs((targetY + fieldHeight) - (otherField.y + otherHeight)) <= snapDistance) {
        snappedY = otherField.y + otherHeight - fieldHeight;
        snapGuidesY.push(otherField.y + otherHeight);
      }
    }
    
    // Update snap guides
    setSnapGuides({ x: snapGuidesX, y: snapGuidesY });
    
    // Ensure snapped position doesn't cause overlap
    const snappedRect = { x: snappedX, y: snappedY, width: fieldWidth, height: fieldHeight };
    if (isOverlappingOtherFields(snappedRect, field.page, field.id)) {
      setSnapGuides({ x: [], y: [] }); // Clear guides if snapping causes overlap
      return { x: targetX, y: targetY }; // Return original if snapping causes overlap
    }
    
    return { x: snappedX, y: snappedY };
  }, [signatureFields, formFields, isOverlappingOtherFields]);
  
  // Refs
  const containerRef = useRef(null);
  // const pageRef = useRef(null);
  const pageWrapperRef = useRef(null);
  
  // Track PDF page dimensions for coordinate conversion
  const [pdfPageDimensions, setPdfPageDimensions] = useState(null);
  const [renderedPageDimensions, setRenderedPageDimensions] = useState(null);
  
  // Load document and contacts on component mount
  useEffect(() => {
    const loadDocument = async () => {
      if (!documentId) {
        setError('No document ID provided');
        setLoading(false);
        return;
      }

      try {
        console.log('Loading document:', documentId);
        const document = await documentService.getDocument(documentId);
        setDocumentData(document);
        
        // Get the file URL for the PDF viewer
        const url = await documentService.getDocumentFileUrl(documentId);
        setFileUrl(url);
        
        // Load existing form fields if any
        if (document.formFields) {
          try {
            const existingFormFields = JSON.parse(document.formFields);
            setFormFields(existingFormFields);
          } catch (err) {
            console.error('Error parsing form fields:', err);
          }
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading document:', err);
        setError('Failed to load document. Please try again.');
        setLoading(false);
      }
    };

    const loadContacts = async () => {
      setLoadingContacts(true);
      try {
        const contactsData = await contactService.getContacts(1, 100);
        setUserContacts(contactsData.contacts || []);
      } catch (err) {
        console.error('Error loading contacts:', err);
      } finally {
        setLoadingContacts(false);
      }
    };

    loadDocument();
    loadContacts();
  }, [documentId]);

  // PDF event handlers
  const onDocumentLoadSuccess = (pdf) => {
    setNumPages(pdf.numPages);
    setPdfDocument(pdf);
    console.log('PDF loaded successfully with', pdf.numPages, 'pages');
    
    // Auto-scale to fit the container when document loads
    if (autoScale && containerRef.current) {
      setTimeout(() => {
        const container = containerRef.current;
        const containerWidth = container.clientWidth - 100; // Account for padding
        const pageWidth = 800; // Approximate PDF page width
        const calculatedScale = Math.min(1.0, containerWidth / pageWidth);
        setScale(calculatedScale);
        setAutoScale(false); // Disable auto-scale after initial load
      }, 100);
    }

    // Extract positioned text data from all pages
    extractPositionedTextFromAllPages(pdf);
  };

  // Add handler to track PDF page dimensions
  const onPageLoadSuccess = (page) => {
    const viewport = page.getViewport({ scale: 1 });
    setPdfPageDimensions({
      width: viewport.width,
      height: viewport.height
    });
    console.log(`📐 PDF Page dimensions: ${viewport.width}x${viewport.height} points`);
    
    // Track rendered dimensions
    setTimeout(() => {
      const pageEl = pageWrapperRef.current?.querySelector('.react-pdf__Page');
      if (pageEl) {
        const rect = pageEl.getBoundingClientRect();
        setRenderedPageDimensions({
          width: rect.width / scale,
          height: rect.height / scale
        });
        console.log(`📐 Rendered dimensions: ${rect.width / scale}x${rect.height / scale}px`);
      }
    }, 100);
  };

  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    setError('Failed to load PDF document');
  };

  // Extract positioned text data from all pages of the PDF
  const extractPositionedTextFromAllPages = async (pdfDoc) => {
    try {
      console.log('🔍 Extracting positioned text from PDF...');
      const allTextItems = [];
      
      for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
        const page = await pdfDoc.getPage(pageNum);
        const textContent = await page.getTextContent();
        const viewport = page.getViewport({ scale: 1.0 });
        
        // Convert text items to our format with coordinate conversion
        textContent.items.forEach((item) => {
          if (item.str && item.str.trim()) { // Only include non-empty text
            // Convert from PDF bottom-left to top-left coordinate system
            // This matches what DocumentViewer expects
            const originalY = item.transform[5];
            const topLeftY = viewport.height - originalY - item.height;
            
            // Debug coordinate transformation for problematic text (same as backend)
            if (item.str.includes('Rental Company Name') || item.str.includes('Lessor') || item.str.includes('Representative Name')) {
              console.log(`🔍 Frontend coordinate debug for: "${item.str}"`);
              console.log(`  📏 Viewport: ${viewport.width} × ${viewport.height}`);
              console.log(`  📍 Original PDF coords: x=${item.transform[4]}, y=${originalY} (bottom-left)`);
              console.log(`  🔄 Converted coords: x=${item.transform[4]}, y=${topLeftY} (top-left)`);
              console.log(`  📦 Item height: ${item.height}`);
              console.log(`  🧮 Formula: ${viewport.height} - ${originalY} - ${item.height} = ${topLeftY}`);
            }
            
            allTextItems.push({
              page: pageNum,
              x: item.transform[4], // X coordinate 
              y: topLeftY, // Y coordinate (CONVERTED to top-left)
              width: item.width,
              height: item.height,
              text: item.str,
              fontName: item.fontName,
              fontSize: Math.abs(item.transform[0]), // Font size from transform matrix
              // Store debugging data
              originalY: originalY,
              topLeftY: topLeftY,
              viewport: { width: viewport.width, height: viewport.height }
            });
          }
        });
      }
      
      console.log(`📄 Extracted ${allTextItems.length} text items from ${pdfDoc.numPages} pages`);
      // setPositionedTextData(allTextItems);
      
      return allTextItems;
    } catch (error) {
      console.error('❌ Error extracting positioned text:', error);
      return [];
    }
  };

  // Navigation handlers
  const goToPrevPage = () => {
    setPageNumber(Math.max(1, pageNumber - 1));
  };

  const goToNextPage = () => {
    setPageNumber(Math.min(numPages, pageNumber + 1));
  };

  // Renamed functions to match DocumentViewer naming
  const previousPage = goToPrevPage;
  const nextPage = goToNextPage;

  // Zoom handlers
  const zoomIn = () => {
    setScale(Math.min(3.0, scale + 0.2));
  };

  const zoomOut = () => {
    setScale(Math.max(0.5, scale - 0.2));
  };

  // Field management - now handles click-to-place
  const addFieldAtPosition = (x, y) => {
    console.log('addFieldAtPosition called, currentFieldType:', currentFieldType, 'position:', x, y);
    
    // Get currently selected contact for auto-assignment
    const autoAssignContact = getCurrentlySelectedContact();
    
    if (currentFieldType === 'signature') {
      const newField = {
        id: `field-${Date.now()}`,
        type: 'signature',
        page: pageNumber,
        x: x - 150, // Offset to center the field on click position
        y: y - 50,  // Offset to center vertically
        width: 250,
        height: 80, // Adjusted default size
        required: true,
        assignedTo: autoAssignContact, // Auto-assign if contact slot is selected
        label: `Signature ${signatureFields.length + 1}`,
        signatureId: `SIG-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}` // Generate unique signature ID
      };
      
      console.log('Adding new signature field:', newField, autoAssignContact ? `auto-assigned to ${autoAssignContact.name}` : 'unassigned');
      setSignatureFields(prev => {
        const updated = [...prev, newField];
        console.log('Updated signatureFields:', updated);
        return updated;
      });
    } else if (currentFieldType === 'form') {
      const newField = {
        id: `form-field-${Date.now()}`,
        type: selectedFormFieldType,
        page: pageNumber,
        x: x - 75, // Offset to center the field on click position
        y: y - 25, // Offset to center vertically
        width: 150,
        height: 50, // Taller for contact assignment
        required: false,
        assignedTo: autoAssignContact, // Auto-assign if contact slot is selected
        placeholder: (FORM_FIELD_TYPES[selectedFormFieldType] || FORM_FIELD_TYPES.text).placeholder
      };
      
      console.log('Adding new form field:', newField, autoAssignContact ? `auto-assigned to ${autoAssignContact.name}` : 'unassigned');
      setFormFields(prev => {
        const updated = [...prev, newField];
        console.log('Updated formFields:', updated);
        return updated;
      });
    }
  };

  // Legacy addField for backward compatibility (centers the field)
  // Legacy addField retained for backward compatibility (unused)

  // Keyboard shortcut handlers
  const toggleFieldType = useCallback(() => {
    setCurrentFieldType(prev => prev === 'signature' ? 'form' : 'signature');
    console.log('Toggled field type via TAB key');
  }, []);

  const cycleFormFieldType = useCallback(() => {
    if (currentFieldType === 'form') {
      const formFieldTypes = Object.keys(FORM_FIELD_TYPES);
      const currentIndex = formFieldTypes.indexOf(selectedFormFieldType);
      const nextIndex = (currentIndex + 1) % formFieldTypes.length;
      setSelectedFormFieldType(formFieldTypes[nextIndex]);
      console.log('Cycled form field type via SHIFT key:', formFieldTypes[nextIndex]);
    }
  }, [currentFieldType, selectedFormFieldType]);

  // Document interaction state for placing fields
  const [isPlacingField, setIsPlacingField] = useState(false);
  const [placementStartPos, setPlacementStartPos] = useState({ x: 0, y: 0 });
  const [placementStartTime, setPlacementStartTime] = useState(0);
  const [tempField, setTempField] = useState(null);

  // Document click/drag handlers for placing fields
  const handleDocumentMouseDown = useCallback((e) => {
    console.log('🖱️ Mouse down detected on document', {
      target: e.target,
      currentTarget: e.currentTarget,
      scale,
      currentFieldType,
      isDragging,
      isResizing
    });

    // Don't place fields if clicking on existing fields or UI elements
    if (e.target.closest('[data-field-overlay]') || 
        e.target.closest('button') || 
        isDragging || isResizing) {
      console.log('❌ Ignoring click - hitting overlay/button or currently dragging');
      return;
    }

    if (!pageWrapperRef.current) {
      console.log('❌ No pageWrapperRef available');
      return;
    }

    const pageRect = pageWrapperRef.current.getBoundingClientRect();
    const clickX = (e.clientX - pageRect.left) / scale;
    const clickY = (e.clientY - pageRect.top) / scale;

    console.log('📐 Calculated click position:', {
      pageRect,
      clientX: e.clientX,
      clientY: e.clientY,
      clickX,
      clickY,
      scale
    });

    // Ensure the field stays within page bounds
    const maxX = (pageRect.width / scale) - (currentFieldType === 'signature' ? 300 : 150);
    const maxY = (pageRect.height / scale) - (currentFieldType === 'signature' ? 100 : 50);
    
    const boundedX = Math.max(0, Math.min(clickX, maxX));
    const boundedY = Math.max(0, Math.min(clickY, maxY));

    console.log('✅ Placing field at:', { boundedX, boundedY, currentFieldType });

    setIsPlacingField(true);
    setPlacementStartPos({ x: boundedX, y: boundedY });
    setPlacementStartTime(Date.now());

    // Create temporary field for preview during drag
    const minWidth = currentFieldType === 'signature' ? 100 : 75;
    const minHeight = currentFieldType === 'signature' ? 35 : 25;

    const newTempField = {
      id: 'temp-placement',
      type: currentFieldType === 'signature' ? 'signature' : selectedFormFieldType,
      page: pageNumber,
      x: boundedX,
      y: boundedY,
      width: minWidth,
      height: minHeight,
      isTemporary: true
    };

    setTempField(newTempField);
    
    e.preventDefault();
    e.stopPropagation();
  }, [scale, currentFieldType, isDragging, isResizing, pageNumber, selectedFormFieldType]);

  const handleDocumentMouseMove = useCallback((e) => {
    if (!isPlacingField || !pageWrapperRef.current || !tempField) return;

    const pageRect = pageWrapperRef.current.getBoundingClientRect();
    const currentX = (e.clientX - pageRect.left) / scale;
    const currentY = (e.clientY - pageRect.top) / scale;

    // Calculate new width and height
    let newWidth = Math.max(
      currentFieldType === 'signature' ? 80 : 75, // Minimum width
      Math.abs(currentX - placementStartPos.x)
    );
    let newHeight = Math.max(
      currentFieldType === 'signature' ? 30 : 25, // Minimum height
      Math.abs(currentY - placementStartPos.y)
    );

    // Apply 2:3 minimum aspect ratio constraint (width should be at least 1.5x height)
    const minRequiredWidth = newHeight * 1.5;
    if (newWidth < minRequiredWidth) {
      newHeight = newWidth / 1.5;
    }

    // Apply maximum boundary constraints to prevent infinite dragging
    const fieldX = Math.min(placementStartPos.x, currentX);
    const fieldY = Math.min(placementStartPos.y, currentY);
    
    // Max constraints based on page size and field position
    const maxWidth = (pageRect.width / scale) - fieldX;
    const maxHeight = (pageRect.height / scale) - fieldY;
    
    newWidth = Math.min(newWidth, maxWidth - 10); // Leave 10px margin
    newHeight = Math.min(newHeight, maxHeight - 10); // Leave 10px margin
    
    // Also set reasonable maximum sizes (same as resize logic)
    newWidth = Math.min(newWidth, 300); // Max 300px width
    newHeight = Math.min(newHeight, 105); // Max 105px height
    
    // Re-apply aspect ratio constraint after max constraints
    const minRequiredWidthAfter = newHeight * 1.5;
    if (newWidth < minRequiredWidthAfter) {
      newHeight = newWidth / 1.5;
    }

    // Check for collisions and adjust position if needed
    // Use start position as anchor - only flip sides when dragged far enough
    const minWidth = currentFieldType === 'signature' ? 100 : 75;
    const minHeight = currentFieldType === 'signature' ? 35 : 25;
    
    const deltaX = currentX - placementStartPos.x;
    const deltaY = currentY - placementStartPos.y;
    
    // Determine size based on drag direction and minimum requirements
    let width = minWidth;
    let height = minHeight;
    let isLeftAnchor = true; // Track which side is anchored
    let isTopAnchor = true;
    
    // Handle horizontal direction
    if (Math.abs(deltaX) >= minWidth) {
      // Dragged far enough to use actual drag distance
      if (deltaX > 0) {
        // Dragging right - start is left anchor
        width = deltaX;
        isLeftAnchor = true;
      } else {
        // Dragging left - start becomes right anchor
        width = Math.abs(deltaX);
        isLeftAnchor = false;
      }
    } else if (deltaX > 0) {
      // Dragging right but not far enough - grow right from start
      width = Math.max(minWidth, deltaX);
      isLeftAnchor = true;
    } else {
      // Dragging left but not far enough - still grow right from start
      width = minWidth;
      isLeftAnchor = true;
    }
    
    // Handle vertical direction
    if (Math.abs(deltaY) >= minHeight) {
      // Dragged far enough to use actual drag distance
      if (deltaY > 0) {
        // Dragging down - start is top anchor
        height = deltaY;
        isTopAnchor = true;
      } else {
        // Dragging up - start becomes bottom anchor
        height = Math.abs(deltaY);
        isTopAnchor = false;
      }
    } else if (deltaY > 0) {
      // Dragging down but not far enough - grow down from start
      height = Math.max(minHeight, deltaY);
      isTopAnchor = true;
    } else {
      // Dragging up but not far enough - still grow down from start
      height = minHeight;
      isTopAnchor = true;
    }

    // Apply maximum constraints while preserving anchor behavior
    // Calculate max constraints based on anchor positions
    let maxWidthForPosition, maxHeightForPosition;
    
    if (isLeftAnchor) {
      maxWidthForPosition = (pageRect.width / scale) - placementStartPos.x - 10;
    } else {
      maxWidthForPosition = placementStartPos.x - 10;
    }
    
    if (isTopAnchor) {
      maxHeightForPosition = (pageRect.height / scale) - placementStartPos.y - 10;
    } else {
      maxHeightForPosition = placementStartPos.y - 10;
    }
    
    // Apply constraints
    width = Math.min(width, maxWidthForPosition);
    height = Math.min(height, maxHeightForPosition);
    width = Math.min(width, 300); // Max 300px width
    height = Math.min(height, 105); // Max 105px height
    
    // Re-apply aspect ratio constraint after max constraints
    const minRequiredWidthFinal = height * 1.5;
    if (width < minRequiredWidthFinal) {
      height = width / 1.5;
    }
    
    // Calculate final position based on anchor points
    let x, y;
    if (isLeftAnchor) {
      x = placementStartPos.x;
    } else {
      x = placementStartPos.x - width;
    }
    
    if (isTopAnchor) {
      y = placementStartPos.y;
    } else {
      y = placementStartPos.y - height;
    }

    const candidateRect = { 
      x, 
      y, 
      width, 
      height: height + (currentFieldType === 'signature' ? 25 : 0) // Add 25px for signature ID bar
    };
    
    // For field placement, preserve Y coordinate even if there's overlap
    // Only adjust X coordinate if needed to avoid major overlaps
    let finalRect = candidateRect;
    if (isOverlappingOtherFields(candidateRect, pageNumber)) {
      // Try to adjust only X coordinate while preserving Y
      const adjustedX = candidateRect.x + 20; // Move slightly to the right
      const xAdjustedRect = { ...candidateRect, x: adjustedX };
      
      if (!isOverlappingOtherFields(xAdjustedRect, pageNumber)) {
        finalRect = xAdjustedRect;
      } else {
        // If X adjustment doesn't work, allow overlap and keep user's intended position
        finalRect = candidateRect;
      }
    }
    
    // Update temporary field
    setTempField(prev => ({
      ...prev,
      x: finalRect.x,
      y: finalRect.y,
      width: finalRect.width,
      height: finalRect.height
    }));
  }, [isPlacingField, scale, currentFieldType, placementStartPos, tempField, isOverlappingOtherFields, findNonOverlappingPosition, pageNumber]);

  const handleDocumentMouseUp = useCallback((e) => {
    console.log('🖱️ Mouse up detected', { isPlacingField, tempField });
    
    if (!isPlacingField || !tempField) {
      console.log('❌ Not placing field or no temp field');
      return;
    }

    const timeDiff = Date.now() - placementStartTime;
    const pageRect = pageWrapperRef.current.getBoundingClientRect();
    const currentX = (e.clientX - pageRect.left) / scale;
    const currentY = (e.clientY - pageRect.top) / scale;
    
    const distanceMoved = Math.sqrt(
      Math.pow(currentX - placementStartPos.x, 2) + 
      Math.pow(currentY - placementStartPos.y, 2)
    );

    // Determine if this was a click (short time + small distance) or drag
    const wasClick = timeDiff < 200 && distanceMoved < 10;

    console.log('📊 Placement analysis:', {
      timeDiff,
      distanceMoved,
      wasClick,
      currentFieldType,
      tempField
    });

    let finalWidth, finalHeight;
    if (wasClick) {
      // Use default sizes for click
      finalWidth = currentFieldType === 'signature' ? 300 : 150;
      finalHeight = currentFieldType === 'signature' ? 100 : 50;
    } else {
      // Use dragged size
      finalWidth = tempField.width;
      finalHeight = tempField.height;
    }

    // Ensure final position is collision-free
    const finalRect = {
      x: tempField.x,
      y: tempField.y,
      width: finalWidth,
      height: finalHeight
    };
    
    const nonOverlappingRect = isOverlappingOtherFields(finalRect, pageNumber) 
      ? findNonOverlappingPosition(finalRect, pageNumber)
      : finalRect;

    // Get currently selected contact for auto-assignment
    const autoAssignContact = getCurrentlySelectedContact();
    
    // Create the actual field
    if (currentFieldType === 'signature') {
      const newField = {
        id: `field-${Date.now()}`,
        type: 'signature',
        page: pageNumber,
        x: nonOverlappingRect.x,
        y: nonOverlappingRect.y,
        width: nonOverlappingRect.width,
        height: nonOverlappingRect.height,
        required: true,
        assignedTo: autoAssignContact, // Auto-assign if contact slot is selected
        label: `Signature ${signatureFields.length + 1}`
      };
      
      console.log('➕ Adding signature field:', newField, autoAssignContact ? `auto-assigned to ${autoAssignContact.name}` : 'unassigned');
      setSignatureFields(prev => [...prev, newField]);
    } else {
      const newField = {
        id: `form-field-${Date.now()}`,
        type: selectedFormFieldType,
        page: pageNumber,
        x: nonOverlappingRect.x,
        y: nonOverlappingRect.y,
        width: nonOverlappingRect.width,
        height: nonOverlappingRect.height,
        required: false,
        assignedTo: autoAssignContact, // Auto-assign if contact slot is selected
        placeholder: (FORM_FIELD_TYPES[selectedFormFieldType] || FORM_FIELD_TYPES.text).placeholder
      };
      
      console.log('➕ Adding form field:', newField, autoAssignContact ? `auto-assigned to ${autoAssignContact.name}` : 'unassigned');
      setFormFields(prev => [...prev, newField]);
    }

    // Clean up
    console.log('🧹 Cleaning up placement state');
    setIsPlacingField(false);
    setTempField(null);
    setPlacementStartPos({ x: 0, y: 0 });
    setPlacementStartTime(0);
  }, [isPlacingField, tempField, placementStartTime, placementStartPos, scale, currentFieldType, pageNumber, signatureFields.length, selectedFormFieldType, isOverlappingOtherFields, findNonOverlappingPosition]);

  const deleteField = (fieldId) => {
    // Try to delete from signature fields first
    setSignatureFields(prev => {
      const filtered = prev.filter(field => field.id !== fieldId);
      return filtered;
    });
    
    // Also try to delete from form fields
    setFormFields(prev => {
      const filtered = prev.filter(field => field.id !== fieldId);
      return filtered;
    });
    
    // Remove from signed fields if it was signed
    setSignedFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(fieldId);
      return newSet;
    });
    
    if (activeField === fieldId) {
      setActiveField(null);
    }
  };

  // Handle immediate signing - using same logic as DocumentViewer.jsx
  const handleSignField = async (signatureData) => {
    if (!fieldToSign) return;
    
    try {
      // Check if user has a default signature (same as DocumentViewer.jsx)
      const response = await fetch(`${API_URL}/users/default-signature`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      console.log('Default signature API response:', data);
      
      if (response.ok && data.defaultSignature) {
        // User has a default signature - apply it directly (same as DocumentViewer.jsx)
        const defaultSig = data.defaultSignature;
        const signatureToApply = {
          signatureData: defaultSig.data,
          signatureType: defaultSig.type,
          fieldId: fieldToSign.id,
          position: {
            x: fieldToSign.x !== undefined ? fieldToSign.x : 100,
            y: fieldToSign.y !== undefined ? fieldToSign.y : 100,
            width: fieldToSign.width || 300,
            height: fieldToSign.height || 100
          },
          page: fieldToSign.page || pageNumber,
          userId: fieldToSign.userId,
          userName: fieldToSign.userName,
          isAppliedButNotFinalized: true // Flag to indicate this needs finalization
        };
        
        // Call onSignatureAdded if available (passed from parent)
        if (onSignatureAdded) {
          onSignatureAdded(signatureToApply);
        }
        
        // Mark field as signed locally
        setSignedFields(prev => new Set([...prev, fieldToSign.id]));
        
        console.log('Applied default signature directly to field:', fieldToSign.id);
        setShowSignatureModal(false);
        setFieldToSign(null);
        return;
      }
    } catch (error) {
      console.error('Error checking for default signature:', error);
    }

    // No default signature found - open signature creation interface
    console.log('No default signature found, opening signature creation interface for field:', fieldToSign.id);
    
    // Close the modal first
    setShowSignatureModal(false);
    
    // Set the field as selected and trigger signature placement
    if (onFieldClick) {
      onFieldClick(fieldToSign, false); // false = not edit mode, normal signature creation
    } else {
      // Fallback: open our own signature placement interface
      console.log('onFieldClick not available, opening internal signature placement');
      setSelectedSignatureField(fieldToSign);
      setShowSignaturePlacement(true);
    }
    
    setFieldToSign(null);
  };

  const handleSkipSigning = () => {
    setShowSignatureModal(false);
    setFieldToSign(null);
  };

  // Handle signature added from SignaturePlacement component
  const handleSignatureAddedInternal = (signatureData) => {
    console.log('Signature added internally:', signatureData);
    
    // Mark field as signed locally
    if (selectedSignatureField) {
      setSignedFields(prev => new Set([...prev, selectedSignatureField.id]));
    }
    
    // Call parent callback if available
    if (onSignatureAdded) {
      onSignatureAdded(signatureData);
    }
    
    // Close signature placement
    setShowSignaturePlacement(false);
    setSelectedSignatureField(null);
  };

  // Field interaction handlers - Optimized with useCallback
  const handleFieldClick = useCallback((field, event) => {
    event.stopPropagation();
    
    // Only show assignment modal if we haven't dragged significantly
    if (!hasDraggedSignificantly) {
      console.log('Field clicked (not dragged):', field);
      setSelectedFieldForAssignment(field);
      setShowAssignmentModal(true);
    }
  }, [hasDraggedSignificantly]);

  const handleFieldMouseDown = useCallback((e, fieldId) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Find field in either signature fields or form fields
    const field = signatureFields.find(f => f.id === fieldId) || formFields.find(f => f.id === fieldId);
    if (!field || !pageWrapperRef.current) return;
    
    const fieldRect = e.currentTarget.getBoundingClientRect();
    
    setActiveField(fieldId);
    setIsDragging(true);
    setHasDraggedSignificantly(false);
    
    // Store initial positions
    setDragStartPosition({
      x: e.clientX,
      y: e.clientY
    });
    
    // Calculate offset relative to the field's current position
    setDragOffset({
      x: e.clientX - fieldRect.left,
      y: e.clientY - fieldRect.top
    });
  }, [signatureFields, formFields]);

  const handleResizeMouseDown = useCallback((e, fieldId) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Find field in either signature fields or form fields
    const field = signatureFields.find(f => f.id === fieldId) || formFields.find(f => f.id === fieldId);
    if (!field) return;
    
    setActiveField(fieldId);
    setIsResizing(true);
    setHasDraggedSignificantly(false);
    
    // Store initial positions and size
    setDragStartPosition({
      x: e.clientX,
      y: e.clientY
    });
    
    setResizeStartSize({
      width: field.width || (field.type === 'signature' ? 200 : 150),
      height: field.height || (field.type === 'signature' ? 70 : 50)
    });
  }, [signatureFields, formFields]);

  const handleMouseMove = useCallback((e) => {
    if ((!isDragging && !isResizing) || !activeField || !pageWrapperRef.current) return;
    
    e.preventDefault();
    
    // Check if we've moved significantly (more than 5px) to distinguish drag from click
    const distanceMoved = Math.abs(e.clientX - dragStartPosition.x) + Math.abs(e.clientY - dragStartPosition.y);
    if (distanceMoved > 5 && !hasDraggedSignificantly) {
      setHasDraggedSignificantly(true);
    }
    
    const pageRect = pageWrapperRef.current.getBoundingClientRect();
    
    // Determine active field - check both signature and form fields
    const fieldObj = signatureFields.find(f => f.id === activeField) || formFields.find(f => f.id === activeField) || {};
    
    if (isResizing) {
      // Handle resizing
      const deltaX = e.clientX - dragStartPosition.x;
      const deltaY = e.clientY - dragStartPosition.y;
      
      // Calculate new dimensions
      let newWidth = Math.max(80, resizeStartSize.width + (deltaX / scale)); // Min 80px width
      let newHeight = Math.max(30, resizeStartSize.height + (deltaY / scale)); // Min 30px height
      
      // Apply 2:3 minimum aspect ratio constraint (width should be at least 1.5x height)
      const minRequiredWidth = newHeight * 1.5;
      if (newWidth < minRequiredWidth) {
        newHeight = newWidth / 1.5;
      }
      
      // Max constraints based on page size and field position
      const maxWidth = (pageRect.width / scale) - (fieldObj.x || 0);
      const maxHeight = (pageRect.height / scale) - (fieldObj.y || 0);
      
      newWidth = Math.min(newWidth, maxWidth - 10); // Leave 10px margin
      newHeight = Math.min(newHeight, maxHeight - 10); // Leave 10px margin
      
      // Also set reasonable maximum sizes
      newWidth = Math.min(newWidth, 300); // Max 300px width
      newHeight = Math.min(newHeight, 105); // Max 150px height
      
      // Re-apply aspect ratio constraint after max constraints
      const minRequiredWidthAfter = newHeight * 1.5;
      if (newWidth < minRequiredWidthAfter) {
        newHeight = newWidth / 1.5;
      }
      
      // Prevent overlap with other fields
      const candidateRectResize = { 
        x: fieldObj.x, 
        y: fieldObj.y, 
        width: newWidth, 
        height: newHeight + (fieldObj.type === 'signature' ? 25 : 0) // Add 25px for signature ID bar
      };
      if (isOverlappingOtherFields(candidateRectResize, fieldObj.page, activeField)) {
        return; // Skip resizing if overlap detected
      }
      
      // Use requestAnimationFrame for smoother updates
      requestAnimationFrame(() => {
        const finalWidth = showGrid 
          ? Math.round(newWidth / 20) * 20 // Snap to 20px grid when grid is enabled
          : Math.round(newWidth * 100) / 100; // Round to 2 decimal places for precision
        const finalHeight = showGrid 
          ? Math.round(newHeight / 20) * 20 // Snap to 20px grid when grid is enabled
          : Math.round(newHeight * 100) / 100; // Round to 2 decimal places for precision

        // Update signature fields
        setSignatureFields(prev =>
          prev.map(field =>
            field.id === activeField
              ? { ...field, width: finalWidth, height: finalHeight }
              : field
          )
        );
        
        // Update form fields
        setFormFields(prev =>
          prev.map(field =>
            field.id === activeField
              ? { ...field, width: finalWidth, height: finalHeight }
              : field
          )
        );
      });
    } else if (isDragging) {
      // Handle dragging (position change)
      const fieldWidth = fieldObj.width || (fieldObj.type === 'signature' ? 200 : 150);
      const fieldHeight = fieldObj.height || (fieldObj.type === 'signature' ? 70 : 50);

      const newX = Math.max(0, Math.min(
        (e.clientX - pageRect.left - dragOffset.x) / scale,
        (pageRect.width / scale) - fieldWidth
      ));
      const newY = Math.max(0, Math.min(
        (e.clientY - pageRect.top - dragOffset.y) / scale,
        (pageRect.height / scale) - fieldHeight
      ));

      // Check which directions of movement are allowed
      const { canMoveX, canMoveY } = getAllowedMovement(fieldObj, newX, newY);
      
      // Check if there would be any collision at all
      const wouldCollide = !canMoveX || !canMoveY || 
        isOverlappingOtherFields({ 
          x: newX, 
          y: newY, 
          width: fieldWidth, 
          height: fieldHeight + (fieldObj.type === 'signature' ? 25 : 0) // Add 25px for signature ID bar
        }, fieldObj.page, activeField);
      
      // Show collision warning if there's any restriction
      setCollisionWarning(wouldCollide);
      
      // Apply smart collision detection - allow movement in non-blocked directions
      let finalX = fieldObj.x;
      let finalY = fieldObj.y;
      
      if (canMoveX) {
        finalX = newX;
      }
      
      if (canMoveY) {
        finalY = newY;
      }
      
      // If neither direction is blocked, check if both together would cause collision
      if (canMoveX && canMoveY) {
        const combinedRect = { 
          x: newX, 
          y: newY, 
          width: fieldWidth, 
          height: fieldHeight + (fieldObj.type === 'signature' ? 25 : 0) // Add 25px for signature ID bar
        };
        if (isOverlappingOtherFields(combinedRect, fieldObj.page, activeField)) {
          // Both directions together cause collision, use individual directions only
          finalX = fieldObj.x;
          finalY = fieldObj.y;
          
          // Try X movement alone
          if (!isOverlappingOtherFields({ 
            x: newX, 
            y: fieldObj.y, 
            width: fieldWidth, 
            height: fieldHeight + (fieldObj.type === 'signature' ? 25 : 0) // Add 25px for signature ID bar
          }, fieldObj.page, activeField)) {
            finalX = newX;
          }
          
          // Try Y movement alone
          if (!isOverlappingOtherFields({ 
            x: fieldObj.x, 
            y: newY, 
            width: fieldWidth, 
            height: fieldHeight + (fieldObj.type === 'signature' ? 25 : 0) // Add 25px for signature ID bar
          }, fieldObj.page, activeField)) {
            finalY = newY;
          }
        } else {
          // No collision with combined movement, check for snapping
          const snappedPos = getSnappedPosition(fieldObj, newX, newY);
          finalX = snappedPos.x;
          finalY = snappedPos.y;
        }
      } else {
        // Apply snapping for allowed movements
        if (canMoveX && !canMoveY) {
          const snappedPos = getSnappedPosition(fieldObj, newX, fieldObj.y);
          finalX = snappedPos.x;
        } else if (!canMoveX && canMoveY) {
          const snappedPos = getSnappedPosition(fieldObj, fieldObj.x, newY);
          finalY = snappedPos.y;
        }
      }
      
      // Use requestAnimationFrame for smoother updates
      requestAnimationFrame(() => {
        const newXPos = showGrid 
          ? Math.round(finalX / 20) * 20 // Snap to 20px grid when grid is enabled
          : Math.round(finalX * 100) / 100; // Round to 2 decimal places for precision
        const newYPos = showGrid 
          ? Math.round(finalY / 20) * 20 // Snap to 20px grid when grid is enabled
          : Math.round(finalY * 100) / 100; // Round to 2 decimal places for precision

        // Update signature fields
        setSignatureFields(prev =>
          prev.map(field =>
            field.id === activeField
              ? { ...field, x: newXPos, y: newYPos }
              : field
          )
        );
        
        // Update form fields
        setFormFields(prev =>
          prev.map(field =>
            field.id === activeField
              ? { ...field, x: newXPos, y: newYPos }
              : field
          )
        );
      });
    }
  }, [isDragging, isResizing, activeField, dragStartPosition, dragOffset, scale, hasDraggedSignificantly, signatureFields, formFields, resizeStartSize, showGrid, isOverlappingOtherFields, getAllowedMovement, getSnappedPosition]);

  const handleMouseUp = useCallback((e) => {
    if (isDragging || isResizing) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsDragging(false);
    setIsResizing(false);
    setActiveField(null);
    setCollisionWarning(false); // Reset collision warning
    setSnapGuides({ x: [], y: [] }); // Clear snap guides
    
    // Reset drag detection after a short delay to prevent immediate click handling
    setTimeout(() => {
      setHasDraggedSignificantly(false);
    }, 100);
  }, [isDragging, isResizing]);

  // Contact management
  // Refactored: assignFieldToContact now takes a contact object instead of just an ID
  const assignFieldToContact = (contact) => {
    if (!selectedFieldForAssignment) return;

    // Check if this is a contact slot assignment (from the dropdown)
    if (selectedFieldForAssignment.type === 'contact-assignment') {
      const contactSlot = selectedFieldForAssignment.contactSlot;
      const slotIndex = contactSlot - 2; // Convert to 0-based index for non-current-user contacts
      
      // Update contacts array - insert contact at specific position
      setContacts(prev => {
        const newContacts = [...prev];
        const currentUserContact = newContacts.find(c => c.isCurrentUser);
        const otherContacts = newContacts.filter(c => !c.isCurrentUser);
        
        // Remove contact if already exists in another position
        const filteredContacts = otherContacts.filter(c => c.id !== contact.id);
        
        // Insert contact at the specified slot position
        filteredContacts.splice(slotIndex, 0, contact);
        
        // Rebuild contacts array with current user first
        return [currentUserContact, ...filteredContacts];
      });
      
      // Switch dropdown display to the newly assigned contact slot (only if slots 1-3)
      // and update active contact slot for auto-assignment
      if (contactSlot <= 3) {
        setSelectedContactSlot(contactSlot);
      }
      setActiveContactSlot(contactSlot);
    } else {
      // Regular field assignment
      // Update signature fields
      setSignatureFields(prev =>
        prev.map(field =>
          field.id === selectedFieldForAssignment.id
            ? { ...field, assignedTo: contact }
            : field
        )
      );

      // Update form fields
      setFormFields(prev =>
        prev.map(field =>
          field.id === selectedFieldForAssignment.id
            ? { ...field, assignedTo: contact }
            : field
        )
      );

      // Add to local contacts if not present
      setContacts(prev => {
        if (!prev.some(c => c.id === contact.id)) {
          return [...prev, contact];
        }
        return prev;
      });
    }

    setShowAssignmentModal(false);
    
    // If assigning to current user and it's a signature field, offer immediate signing
    // Commented out for later version - will implement sign now popup later
    // if (contact.isCurrentUser && selectedFieldForAssignment.type === 'signature') {
    //   setFieldToSign(selectedFieldForAssignment);
    //   setShowSignatureModal(true);
    // }
    
    setSelectedFieldForAssignment(null);
  };

  // Re-add addNewContact, using the new assignFieldToContact signature
  const addNewContact = async () => {
    if (!newContactName.trim() || !newContactEmail.trim()) {
      setContactCreationError('Please fill in both name and email');
      return;
    }

    if (!newContactEmail.includes('@')) {
      setContactCreationError('Please enter a valid email address');
      return;
    }

    setCreatingContact(true);
    setContactCreationError('');

    try {
      // Create contact in the database
      const createdContact = await contactService.createContact({
        name: newContactName.trim(),
        email: newContactEmail.trim().toLowerCase()
      });

      console.log('✅ Contact created successfully:', createdContact);

      // Add to local contacts list for immediate use
      const newContact = {
        id: createdContact.id,
        name: createdContact.name,
        email: createdContact.email,
        isCurrentUser: false
      };

      setContacts(prev => [...prev, newContact]);
      // Update user contacts list to include the new contact
      setUserContacts(prev => [...prev, createdContact]);

      // Assign immediately
      assignFieldToContact(newContact);

      // Reset form
      setNewContactName('');
      setNewContactEmail('');
      setShowNewContactForm(false);
      setContactCreationError('');
      console.log('✅ Contact added to local state and form reset');

    } catch (error) {
      console.error('❌ Error creating contact:', error);
      // Handle specific error cases
      if (error.response?.status === 409) {
        setContactCreationError('A contact with this email already exists');
      } else if (error.response?.data?.error) {
        setContactCreationError(error.response.data.error);
      } else {
        setContactCreationError('Failed to create contact. Please try again.');
      }
    } finally {
      setCreatingContact(false);
    }
  };

  // Event listeners - Optimized with useCallback dependencies
  useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove, { passive: false });
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  // Document-level event listeners for field placement
  useEffect(() => {
    if (isPlacingField) {
      document.addEventListener('mousemove', handleDocumentMouseMove, { passive: false });
      document.addEventListener('mouseup', handleDocumentMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleDocumentMouseMove);
        document.removeEventListener('mouseup', handleDocumentMouseUp);
      };
    }
  }, [isPlacingField, handleDocumentMouseMove, handleDocumentMouseUp]);

  // Quick assignment function for keyboard shortcuts
  const quickAssignField = useCallback((assignmentIndex) => {
    // Use hoveredField (when mouse is over a field) instead of activeField
    const targetFieldId = hoveredField || activeField;
    if (!targetFieldId) return;

    const fieldObj = [...signatureFields, ...formFields].find(f => f.id === targetFieldId);
    if (!fieldObj) return;

    if (assignmentIndex === 1) {
      // Assign to current user (me)
      const currentUserContact = {
        id: 'current-user',
        name: user?.name || 'Me',
        email: user?.email || '',
        isCurrentUser: true
      };
      
      // Update the field directly without showing modal
      setSignatureFields(prev =>
        prev.map(field =>
          field.id === targetFieldId
            ? { ...field, assignedTo: currentUserContact }
            : field
        )
      );

      setFormFields(prev =>
        prev.map(field =>
          field.id === targetFieldId
            ? { ...field, assignedTo: currentUserContact }
            : field
        )
      );

      console.log(`Quick assigned field ${targetFieldId} to current user`);
    } else {
      // Assign to nth contact (2-9)
      const contactIndex = assignmentIndex - 2; // Convert to 0-based index for contacts array
      const availableContacts = contacts.filter(c => !c.isCurrentUser);
      
      if (contactIndex < availableContacts.length) {
        const selectedContact = availableContacts[contactIndex];
        
        // Update the field directly without showing modal
        setSignatureFields(prev =>
          prev.map(field =>
            field.id === targetFieldId
              ? { ...field, assignedTo: selectedContact }
              : field
          )
        );

        setFormFields(prev =>
          prev.map(field =>
            field.id === targetFieldId
              ? { ...field, assignedTo: selectedContact }
              : field
          )
        );

        console.log(`Quick assigned field ${targetFieldId} to contact: ${selectedContact.name}`);
      } else {
        // No contact at this index, show assignment modal for the contact slot
        const dummyField = {
          id: `contact-slot-${assignmentIndex}`,
          type: 'contact-assignment',
          contactSlot: assignmentIndex,
          label: `Contact Slot ${assignmentIndex}`
        };
        setSelectedFieldForAssignment(dummyField);
        setShowAssignmentModal(true);
        console.log(`No contact at index ${assignmentIndex}, showing contact slot assignment modal`);
      }
    }
  }, [hoveredField, activeField, signatureFields, formFields, contacts, user, setShowAssignmentModal, setSelectedFieldForAssignment]);

  // Keyboard event listeners for shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle shortcuts when the focus is not on input/textarea/select elements
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') {
        return;
      }

      // Handle number keys 0-9 for quick assignment or contact slot assignment
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        
        // Handle "0" key - switch to manual contact mode
        if (e.key === '0') {
          setSelectedContactSlot('manual');
          setActiveContactSlot('manual');
          console.log('Switched to manual contact mode');
          return;
        }
        
        const assignmentIndex = parseInt(e.key);
        
        if (hoveredField || activeField) {
          // If hovering over a field, do field assignment
          quickAssignField(assignmentIndex);
        } else {
          // If not hovering over a field, handle contact slot assignment
          if (assignmentIndex === 1) {
            // Slot 1 is always "Me" - switch both display and active state
            setSelectedContactSlot(1);
            setActiveContactSlot(1);
            console.log('Switched to viewing slot 1 (Me)');
            return;
          }
          
          // Always set the active contact slot for auto-assignment
          setActiveContactSlot(assignmentIndex);
          
          // For slots 2-3: Also update dropdown display
          // For slots 4-9: Keep dropdown on manual, but set active slot for auto-assignment
          if (assignmentIndex <= 3) {
            setSelectedContactSlot(assignmentIndex);
          } else {
            // Slots 4-9 are keyboard-only, keep dropdown on manual
            setSelectedContactSlot('manual');
          }
          
          const availableContacts = contacts.filter(c => !c.isCurrentUser);
          const contactIndex = assignmentIndex - 2; // Convert to 0-based index
          const assignedContact = availableContacts[contactIndex];
          
          if (!assignedContact) {
            // No contact assigned to this slot, open assignment modal
            const dummyField = {
              id: `contact-slot-${assignmentIndex}`,
              type: 'contact-assignment',
              contactSlot: assignmentIndex,
              label: `Contact Slot ${assignmentIndex}`
            };
            setSelectedFieldForAssignment(dummyField);
            setShowAssignmentModal(true);
            console.log(`Opening contact slot assignment modal for slot ${assignmentIndex}`);
          } else {
            // Contact already assigned - just switched to viewing this slot
            console.log(`Switched to viewing slot ${assignmentIndex}: ${assignedContact.name}`);
          }
        }
        return;
      }

      if (e.key === 'Tab') {
        e.preventDefault();
        toggleFieldType();
      } else if (e.key === 'Shift') {
        e.preventDefault();
        cycleFormFieldType();
      } else if ((e.key === 'Backspace' || e.key === 'Delete') && hoveredField) {
        e.preventDefault();
        
        // Check if the hovered field is a signature field
        const signatureField = signatureFields.find(f => f.id === hoveredField);
        // if (signatureField && !signatureField.assignedTo) {
          console.log('🗑️ Deleting unassigned signature field:', signatureField);
          setSignatureFields(prev => prev.filter(f => f.id !== hoveredField));
          setHoveredField(null);
          if (activeField === hoveredField) setActiveField(null);
          return;
        // }
        
        // Check if the hovered field is a form field
        const formField = formFields.find(f => f.id === hoveredField);
        // if (formField && !formField.assignedTo) {
          console.log('🗑️ Deleting unassigned form field:', formField);
          setFormFields(prev => prev.filter(f => f.id !== hoveredField));
          setHoveredField(null);
          if (activeField === hoveredField) setActiveField(null);
          return;
        // }
        
        // If field is assigned, show a message
        // if ((signatureField && signatureField.assignedTo) || (formField && formField.assignedTo)) {
        //   console.log('⚠️ Cannot delete assigned field - it is assigned to someone');
        //   // Could show a toast notification here if you have one
        // }
      } else if (e.key === 'Escape') {
        // Close contact assignment modal if open
        if (showAssignmentModal) {
          e.preventDefault();
          setShowAssignmentModal(false);
          setSelectedFieldForAssignment(null);
          console.log('Contact assignment modal closed via ESC key');
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleFieldType, cycleFormFieldType, hoveredField, signatureFields, formFields, activeField, contacts, user, quickAssignField, showAssignmentModal]);

  // Handle fields detected by document skimmer
  const handleFieldsDetected = (detectedFields) => {
    console.log('Fields detected by document skimmer:', detectedFields);
    
    // Convert detected fields to the component's field format
    const convertedFields = detectedFields.map(field => {
      if (field.type === 'signature') {
        return {
          id: field.id,
          type: 'signature',
          page: field.page,
          x: field.x,
          y: field.y,
          width: field.width,
          height: field.height,
          required: field.required,
          assignedTo: null,
          label: field.label,
          signatureId: field.signatureId || `SIG-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}` // Generate signature ID if not present
        };
      } else {
        return {
          id: field.id,
          type: 'form',
          fieldType: field.type,
          page: field.page,
          x: field.x,
          y: field.y,
          width: field.width,
          height: field.height,
          required: field.required,
          assignedTo: null,
          placeholder: field.placeholder
        };
      }
    });
    
    // Add signature fields
    const newSignatureFields = convertedFields.filter(f => f.type === 'signature');
    if (newSignatureFields.length > 0) {
      setSignatureFields(prev => [...prev, ...newSignatureFields]);
    }
    
    // Add form fields
    const newFormFields = convertedFields.filter(f => f.type === 'form');
    if (newFormFields.length > 0) {
      setFormFields(prev => [...prev, ...newFormFields]);
    }
    
    console.log(`Added ${newSignatureFields.length} signature fields and ${newFormFields.length} form fields from skimmer`);
  };

  // Complete handler
  const handleComplete = async () => {
    if (signatureFields.length === 0 && formFields.length === 0) {
      alert('Please add at least one field');
      return;
    }

    // Check if all signature fields are assigned (form fields are optional)
    const unassignedSignatureFields = signatureFields.filter(field => !field.assignedTo);
    if (unassignedSignatureFields.length > 0) {
      alert('Please assign all signature fields to contacts before completing');
      return;
    }

    try {
      setLoading(true);
      
      // Calculate proper coordinates for each field
      const calculateFieldCoordinates = (field) => {
        if (!pdfPageDimensions) {
          console.warn('PDF dimensions not available for field', field.id);
          return {
            x: field.x,
            y: field.y,
            width: field.width || 200,
            height: field.height || 70,
            coordinateFormat: 'v1'
          };
        }
        
        // Get the actual rendered width (800px on desktop)
        const renderedWidth = window.innerWidth > 1024 ? 800 : (renderedPageDimensions?.width || 800);
        const scaleFactor = pdfPageDimensions.width / renderedWidth;
        
        // Convert pixel coordinates to PDF points
        const pdfX = field.x * scaleFactor;
        const pdfY = field.y * scaleFactor;
        const pdfWidth = (field.width || 200) * scaleFactor;
        const pdfHeight = (field.height || 70) * scaleFactor;
        
        // Calculate normalized coordinates
        const normalizedX = pdfX / pdfPageDimensions.width;
        const normalizedY = pdfY / pdfPageDimensions.height;
        const normalizedWidth = pdfWidth / pdfPageDimensions.width;
        const normalizedHeight = pdfHeight / pdfPageDimensions.height;
        
        console.log(`📐 Field ${field.id} coordinate conversion:`);
        console.log(`   Pixel: (${field.x}, ${field.y}) ${field.width}x${field.height}`);
        console.log(`   PDF points: (${pdfX.toFixed(2)}, ${pdfY.toFixed(2)}) ${pdfWidth.toFixed(2)}x${pdfHeight.toFixed(2)}`);
        console.log(`   Normalized: (${normalizedX.toFixed(3)}, ${normalizedY.toFixed(3)}) ${normalizedWidth.toFixed(3)}x${normalizedHeight.toFixed(3)}`);
        
        return {
          // PDF points
          x: pdfX,
          y: pdfY,
          width: pdfWidth,
          height: pdfHeight,
          // Normalized coordinates
          normalizedX,
          normalizedY,
          normalizedWidth,
          normalizedHeight,
          // Original pixel coordinates
          pixelX: field.x,
          pixelY: field.y,
          pixelWidth: field.width || 200,
          pixelHeight: field.height || 70,
          // Metadata
          renderedPageWidth: renderedWidth,
          pdfPageWidth: pdfPageDimensions.width,
          pdfPageHeight: pdfPageDimensions.height,
          coordinateFormat: 'v2'
        };
      };
      
      // Format the fields and contacts for the backend with precise coordinates
      const formattedData = {
        signatureFields: signatureFields.map(field => ({
          id: field.id,
          type: field.type || 'signature',
          page: field.page,
          ...calculateFieldCoordinates(field),
          required: field.required !== false,
          assignedTo: field.assignedTo,
          isSigned: signedFields.has(field.id) // Include signed status
        })),
        formFields: formFields.map(field => ({
          id: field.id,
          type: field.type,
          page: field.page,
          ...calculateFieldCoordinates(field),
          required: field.required || false,
          assignedTo: field.assignedTo,
          placeholder: field.placeholder
        })),
        contacts: contacts.filter(contact => !contact.isCurrentUser), // Exclude current user from contacts list
        signedFields: Array.from(signedFields) // Include list of signed field IDs
      };
      
      console.log('Completing with formatted data:', formattedData);
      await onComplete(formattedData);
    } catch (error) {
      console.error('Error completing signature fields:', error);
      alert('Failed to create signature request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !documentData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-700">Loading document...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12">
        <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center text-red-600 mb-4">
            <AlertCircle className="mr-2" size={24} />
            <h2 className="text-lg font-semibold">Error</h2>
          </div>
          <p className="text-gray-700 mb-4">{error}</p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 bg-white rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Render different layouts based on embedded prop
  const containerClasses = embedded 
    ? "flex flex-col h-full" 
    : "min-h-screen bg-gray-50 flex flex-col";

  const headerClasses = embedded
    ? "bg-white border-b border-gray-200 px-6 py-4"
    : "bg-white border-b border-gray-200 px-6 py-4 shadow-sm";

  return (
    <div className={containerClasses}>
      {/* Header - only show if not embedded or if we want it */}
      {!embedded && (
        <div className={headerClasses}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Place Signature Fields</h2>
            <button 
              onClick={onCancel}
              className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}
      
      {/* Keyboard shortcuts hint */}
      {showShortcutHint && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6 text-sm">
              <div className="flex items-center space-x-2">
                <kbd className="px-2 py-1 bg-gray-200 rounded text-xs font-mono">TAB</kbd>
                <span className="text-gray-700">Toggle signature/form fields</span>
              </div>
              <div className="flex items-center space-x-2">
                <kbd className="px-2 py-1 bg-gray-200 rounded text-xs font-mono">SHIFT</kbd>
                <span className="text-gray-700">Cycle form field types</span>
              </div>
              <div className="flex items-center space-x-2">
                <kbd className="px-2 py-1 bg-gray-200 rounded text-xs font-mono">0</kbd>
                <span className="text-gray-700">Switch to manual contact mode</span>
              </div>
              <div className="flex items-center space-x-2">
                <kbd className="px-2 py-1 bg-gray-200 rounded text-xs font-mono">1</kbd>
                <span className="text-gray-700">Quick assign field to me</span>
              </div>
              <div className="flex items-center space-x-2">
                <kbd className="px-2 py-1 bg-gray-200 rounded text-xs font-mono">2-9</kbd>
              <span className="text-gray-700">Quick assign field to recent contacts</span>
              </div>
            </div>
            <button
              onClick={() => setShowShortcutHint(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Header with controls - matching DocumentViewer style */}
      <div className="bg-white border-b border-gray-200 p-4 shadow-sm">
        <div className="flex items-center justify-end space-x-4">
            {/* Contact Quick Selector - Single dropdown showing all contacts 1-9 */}
            <div className="relative">
              <select
                value={selectedContactSlot.toString()}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  
                  if (selectedValue === 'manual') {
                    // Manual contact selection
                    setSelectedContactSlot('manual');
                    setActiveContactSlot('manual');
                    return;
                  }
                  
                  const contactNumber = parseInt(selectedValue);
                  
                  // Update both the displayed and active contact slot
                  setSelectedContactSlot(contactNumber);
                  setActiveContactSlot(contactNumber);
                  
                  if (contactNumber === 1) {
                    // Slot 1 is always "Me", just switch display
                    return;
                  }
                  
                  const availableContacts = contacts.filter(c => !c.isCurrentUser);
                  const assignedContact = availableContacts[contactNumber - 2]; // Adjust for 0-based index
                  
                  if (!assignedContact) {
                    // This is an unassigned slot, open assignment modal
                    const dummyField = {
                      id: `contact-slot-${contactNumber}`,
                      type: 'contact-assignment',
                      contactSlot: contactNumber,
                      label: `Contact Slot ${contactNumber}`
                    };
                    setSelectedFieldForAssignment(dummyField);
                    setShowAssignmentModal(true);
                  } else {
                    // Already assigned - just switch to showing this slot
                    console.log(`Switched to viewing contact slot ${contactNumber}: ${assignedContact.name}`);
                  }
                }}
                className={`px-3 py-1.5 text-sm rounded-xl border appearance-none cursor-pointer ${
                  selectedContactSlot === 'manual' 
                    ? 'bg-white text-gray-700' 
                    : 'bg-blue-50 text-blue-700'
                }`}
                style={{ 
                  borderColor: 'rgba(148,163,184,0.2)',
                  paddingLeft: selectedContactSlot === 'manual' ? '0.75rem' : '2rem',  // Less padding for manual, more for numbered slots
                  paddingRight: '1.5rem', // Space for dropdown arrow
                  minWidth: '140px'
                }}
              >
                {/* Manual Contact option (default) */}
                <option value="manual">Manual Contact</option>
                
                {/* Always show slot 1 (Me) */}
                <option value="1">Me</option>
                
                {/* Show slots 2-3 only (4-9 are keyboard-only) */}
                {Array.from({ length: 2 }, (_, index) => {
                  const contactNumber = index + 2;
                  const availableContacts = contacts.filter(c => !c.isCurrentUser);
                  const assignedContact = availableContacts[index];
                  
                  return (
                    <option key={contactNumber} value={contactNumber}>
                      {assignedContact 
                        ? `${assignedContact.name}`
                        : `Unassigned`
                      }
                    </option>
                  );
                })}
              </select>
              {selectedContactSlot !== 'manual' && (
                <span className={`absolute left-2 top-1/2 transform -translate-y-1/2 font-mono font-bold pointer-events-none ${
                  selectedContactSlot === 'manual' ? 'text-gray-700' : 'text-blue-700'
                }`}>
                  {selectedContactSlot}
                </span>
              )}
              <ChevronDown size={12} className={`absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none ${
                selectedContactSlot === 'manual' ? 'text-gray-700' : 'text-blue-700'
              }`} />
            </div>

            {/* Field Type Selector */}
            <div className="flex items-center space-x-2 p-1 rounded-xl border" style={{ borderColor: 'rgba(148,163,184,0.2)', backgroundColor: 'white' }}>
              <button
                onClick={() => setCurrentFieldType('signature')}
                className={`px-3 py-1.5 text-sm rounded-xl flex items-center transition-colors border ${
                  currentFieldType === 'signature'
                    ? 'bg-blue-50 text-blue-700'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
                style={{ borderColor: 'rgba(148,163,184,0.2)' }}
              >
                <FileText size={16} className="mr-1" />
                Signature
              </button>
              {currentFieldType === 'form' ? (
                // Form field mode: Show dropdown with selected field type
                <div className="relative">
                  <select
                    value={selectedFormFieldType}
                    onChange={(e) => setSelectedFormFieldType(e.target.value)}
                    className={`px-3 py-1.5 text-sm rounded-xl flex items-center transition-colors border appearance-none cursor-pointer ${
                      currentFieldType === 'form'
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{ 
                      borderColor: 'rgba(148,163,184,0.2)',
                      paddingLeft: '2.5rem',  // Space for icon
                      paddingRight: '2.5rem' // Space for dropdown arrow
                    }}
                  >
                    {Object.entries(FORM_FIELD_TYPES).map(([type, config]) => (
                      <option key={type} value={type}>
                        {config.label}
                      </option>
                    ))}
                  </select>
                  <Type size={16} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-blue-700 pointer-events-none" />
                  <ChevronDown size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-blue-700 pointer-events-none" />
                </div>
              ) : (
                // Signature mode: Show regular button
                <button
                  onClick={() => setCurrentFieldType('form')}
                  className={`px-3 py-1.5 text-sm rounded-xl flex items-center transition-colors border ${
                    currentFieldType === 'form'
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                  style={{ borderColor: 'rgba(148,163,184,0.2)' }}
                >
                  <Type size={16} className="mr-1" />
                  Form Field
                </button>
              )}
            </div>
            

            {/* Click to Place Indicator */}
            <div className="text-sm text-gray-700 px-3 py-1.5 rounded-xl border bg-white" style={{ borderColor: 'rgba(148,163,184,0.2)' }}>
              Click or drag to place {currentFieldType === 'signature' ? 'signature' : (FORM_FIELD_TYPES[selectedFormFieldType] || FORM_FIELD_TYPES.text).label}
            </div>
            
            <button
              onClick={() => setShowGrid(!showGrid)}
              className={`px-3 py-2 rounded-xl flex items-center border ${
                showGrid ? 'bg-blue-50 text-blue-700' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
              style={{ borderColor: 'rgba(148,163,184,0.2)' }}
            >
              <Settings size={18} className="mr-2" />
              {showGrid ? 'Hide Grid' : 'Show Grid'}
            </button>
        </div>
      </div>
      
      {/* Document content - matching DocumentViewer style */}
      <div className="flex-1 relative overflow-auto bg-gray-50">
        <div 
          ref={containerRef}
          className="relative mx-auto my-8 flex justify-center"
          style={{ maxWidth: '100%' }}
        >
          {fileUrl ? (
            <Document
              file={fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="flex justify-center items-center h-96">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              }
              error={
                <div className="text-center py-10">
                  <p className="text-red-500 mb-2">Failed to load PDF</p>
                  <p className="text-gray-600 text-sm">Please try again or contact support</p>
                </div>
              }
              className="flex justify-center"
            >
              <div ref={pageWrapperRef} className="relative">
                <Page
                  pageNumber={pageNumber}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  scale={scale}
                  className="shadow-lg"
                  width={window.innerWidth > 1024 ? 800 : undefined}
                  onLoadSuccess={onPageLoadSuccess}
                />
                
                {/* Invisible overlay to capture clicks - positioned over the actual PDF page */}
                <div 
                  className="absolute top-0 left-0 w-full h-full z-10"
                  onMouseDown={(e) => {
                    console.log('🎯 Overlay mouse down triggered!', e.target, e.currentTarget);
                    handleDocumentMouseDown(e);
                  }}
                  onMouseUp={(e) => {
                    console.log('🎯 Overlay mouse up triggered!');
                  }}
                  onMouseMove={(e) => {
                    if (isPlacingField) {
                      console.log('🎯 Overlay mouse move during placement');
                    }
                  }}
                  onClick={(e) => {
                    console.log('🎯 Overlay click triggered!', e);
                  }}
                  style={{ 
                    pointerEvents: 'auto',
                    background: 'transparent',
                    cursor: 'crosshair',
                    zIndex: 10
                  }}
                  title="Click to place field"
                />
                  
                  {/* Grid overlay */}
                  {showGrid && (
                    <div 
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        backgroundImage: `
                          linear-gradient(to right, rgba(0,0,0,0.1) 1px, transparent 1px),
                          linear-gradient(to bottom, rgba(0,0,0,0.1) 1px, transparent 1px)
                        `,
                        backgroundSize: `${20 * scale}px ${20 * scale}px`
                      }}
                    />
                  )}
                  
                  {/* Temporary field preview during placement */}
                  {tempField && tempField.page === pageNumber && (tempField.type === 'signature' || currentFieldType === 'signature' || currentFieldType === 'form') && (() => {
                    const isSignature = tempField.type === 'signature' || currentFieldType === 'signature';
                    const isForm = currentFieldType === 'form' && FORM_FIELD_TYPES[tempField.type];

                    // Check if temp field was repositioned due to collisions
                    // Note: We don't show "repositioned" during normal field placement
                    // The collision system automatically adjusts positions when needed
                    const wasRepositioned = false;

                    // Common styles
                    const commonStyles = {
                      left: `${tempField.x * scale}px`,
                      top: `${tempField.y * scale}px`,
                      width: `${tempField.width * scale}px`,
                      height: `${tempField.height * scale}px`
                    };

                    if (isSignature) {
                      return (
                        <div className="absolute z-40 pointer-events-none" style={commonStyles}>
                          <div
                            className={`border-2 border-dashed rounded-xl ${
                              wasRepositioned 
                                ? 'border-orange-500 bg-orange-100/60' 
                                : 'border-blue-500 bg-blue-100/60'
                            }`}
                            style={{
                              width: `${tempField.width * scale}px`,
                              height: `${tempField.height * scale}px`
                            }}
                          >
                            <div className={`flex items-center justify-center h-full text-xs font-medium ${
                              wasRepositioned ? 'text-orange-700' : 'text-blue-700'
                            }`}>
                              {wasRepositioned ? 'Repositioned' : 'Signature Preview'}
                            </div>
                          </div>
                          
                          {/* Signature ID bar for temporary field */}
                          <div className="absolute top-full left-0 w-full mt-1">
                            <div className={`text-xs font-medium truncate px-2 py-1 rounded ${
                              wasRepositioned 
                                ? 'text-orange-700 bg-orange-100' 
                                : 'text-blue-700 bg-blue-100'
                            }`}
                                 style={{ fontSize: '10px' }}>
                              SIGNED: PENDING
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (isForm) {
                      const fieldConfig = FORM_FIELD_TYPES[tempField.type] || FORM_FIELD_TYPES.text;
                      const FieldIcon = fieldConfig.icon;
                      return (
                        <div
                          className={`absolute border-2 border-dashed rounded-lg z-40 pointer-events-none ${
                            wasRepositioned 
                              ? 'border-orange-500 bg-orange-100/60' 
                              : 'border-green-500 bg-green-100/60'
                          }`}
                          style={commonStyles}
                        >
                          <div className={`flex items-center justify-center h-full text-xs font-medium ${
                            wasRepositioned ? 'text-orange-700' : 'text-green-700'
                          }`}>
                            <FieldIcon className="h-3 w-3 mr-1" />
                            {wasRepositioned ? 'Repositioned' : `${fieldConfig.label} Preview`}
                          </div>
                        </div>
                      );
                    }

                    return null; // Return nothing if conditions aren't met
                  })()}

                  {/* Signature fields overlay - Optimized rendering */}
                  {signatureFields
                    .filter(field => field.page === pageNumber)
                    .map((field) => {
                      const isActive = field.id === activeField;
                      const hasAssignment = field.assignedTo;
                      
                      return (
                        <div
                          key={field.id}
                          data-field-overlay="true"
                          className={`absolute border-2 select-none user-select-none pointer-events-auto rounded-xl
                            ${isActive 
                              ? "border-blue-500 bg-blue-100/40 shadow-lg z-40 cursor-move" 
                              : signedFields.has(field.id)
                              ? "border-green-600 bg-green-200/60 z-20"
                              : hasAssignment
                              ? "border-green-500 bg-green-100/40 hover:bg-green-100/60 z-20 cursor-move hover:cursor-move"
                              : "border-red-500 bg-red-100/40 hover:bg-red-100/60 z-20 cursor-move hover:cursor-move"
                            }
                            ${(isDragging || isResizing) && isActive ? 'transition-none' : 'transition-all duration-200 hover:scale-105'}
                          `}
                          style={{
                            left: `${field.x * scale}px`,
                            top: `${field.y * scale}px`,
                                            width: `${(field.width || 200) * scale}px`,
                height: `${(field.height || 50) * scale}px`,
                            willChange: (isDragging || isResizing) && isActive ? 'transform' : 'auto'
                          }}
                          onMouseDown={(e) => handleFieldMouseDown(e, field.id)}
                          onClick={(e) => handleFieldClick(field, e)}
                          onMouseEnter={() => setHoveredField(field.id)}
                          onMouseLeave={() => setHoveredField(null)}
                        >
                          <div className="flex flex-col h-full p-2 text-xs pointer-events-none">
                            {/* Progressive layout based on field dimensions */}
                            {(field.height || 50) >= 70 ? (
                              // Tall field: Show icon + label at top, content below
                              <>
                                {/* Top section with signature icon and label */}
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center space-x-1">
                                    <Pen className={`h-3 w-3 ${
                                      isActive 
                                        ? 'text-blue-600' 
                                        : signedFields.has(field.id)
                                        ? 'text-green-600'
                                        : hasAssignment 
                                          ? 'text-green-600' 
                                          : 'text-red-600'
                                    }`} />
                                    <span className={`font-medium text-xs ${
                                      isActive 
                                        ? 'text-blue-700' 
                                        : signedFields.has(field.id)
                                        ? 'text-green-700'
                                        : hasAssignment 
                                          ? 'text-green-700' 
                                          : 'text-red-700'
                                    }`}>
                                      Signature
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Center section with contact assignment */}
                                <div className="flex-1 flex items-center justify-center">
                                  <div className="text-center w-full max-w-full">
                                    <div className="font-medium text-xs w-full overflow-hidden text-ellipsis whitespace-nowrap">
                                      {signedFields.has(field.id) 
                                        ? '✓ Signed' 
                                        : hasAssignment 
                                          ? field.assignedTo.name 
                                          : 'Click to assign'
                                      }
                                    </div>
                                    {hasAssignment && !signedFields.has(field.id) && (field.height || 50) >= 50 && (
                                      <div className="text-gray-600 text-xs w-full overflow-hidden text-ellipsis whitespace-nowrap">{field.assignedTo.email}</div>
                                    )}
                                    {signedFields.has(field.id) && (
                                      <div className="text-green-700 truncate text-xs">
                                        Ready to send
                                      </div>
                                    )}
                                    {hasAssignment && field.signedAt && (
                                      <div className="text-green-600 truncate text-xs space-y-1">
                                        <div>IP: {field.signedIP || 'N/A'}</div>
                                        <div>Signed: {new Date(field.signedAt).toLocaleDateString()}</div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </>
                            ) : (field.height || 50) >= 50 ? (
                              // Medium height field: Show icon + content centered with email but no "Signature" label
                              <div className="flex-1 flex items-center justify-center">
                                <div className="flex items-center space-x-2 w-full min-w-0">
                                  <Pen className={`h-3 w-3 flex-shrink-0 ${
                                    isActive 
                                      ? 'text-blue-600' 
                                      : signedFields.has(field.id)
                                      ? 'text-green-600'
                                      : hasAssignment 
                                        ? 'text-green-600' 
                                        : 'text-red-600'
                                  }`} />
                                  <div className="text-center w-full max-w-full min-w-0">
                                    <div className="font-medium text-xs w-full overflow-hidden text-ellipsis whitespace-nowrap">
                                      {signedFields.has(field.id) 
                                        ? '✓ Signed' 
                                        : hasAssignment 
                                          ? field.assignedTo.name 
                                          : 'Click to assign'
                                      }
                                    </div>
                                    {hasAssignment && !signedFields.has(field.id) && (
                                      <div className="text-gray-600 text-xs w-full overflow-hidden text-ellipsis whitespace-nowrap">{field.assignedTo.email}</div>
                                    )}
                                    {signedFields.has(field.id) && (
                                      <div className="text-green-700 truncate text-xs">
                                        Ready to send
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : (field.width || 200) >= 150 ? (
                              // Wide but short field: Show icon + label + content on same line
                              <div className="flex-1 flex items-center w-full min-w-0">
                                {/* Icon and label on the left */}
                                <div className="flex-shrink-0 flex items-center space-x-1 mr-3">
                                  <Pen className={`h-3 w-3 ${
                                    isActive 
                                      ? 'text-blue-600' 
                                      : signedFields.has(field.id)
                                      ? 'text-green-600'
                                      : hasAssignment 
                                        ? 'text-green-600' 
                                        : 'text-red-600'
                                  }`} />
                                  <span className={`font-medium text-xs ${
                                    isActive 
                                      ? 'text-blue-700' 
                                      : signedFields.has(field.id)
                                      ? 'text-green-700'
                                      : hasAssignment 
                                        ? 'text-green-700' 
                                        : 'text-red-700'
                                  }`}>
                                    Signature
                                  </span>
                                </div>
                                
                                {/* Content on the right */}
                                <div className="flex-1 text-center min-w-0">
                                  <div className="font-medium text-xs overflow-hidden text-ellipsis whitespace-nowrap">
                                    {signedFields.has(field.id) 
                                      ? '✓ Signed' 
                                      : hasAssignment 
                                        ? field.assignedTo.name 
                                        : 'Click to assign'
                                    }
                                  </div>
                                </div>
                              </div>
                            ) : (
                              // Short field: Show icon and content on same line
                              <div className="flex-1 flex items-center w-full min-w-0">
                                {/* Icon on the left */}
                                <div className="flex-shrink-0 mr-2">
                                  <Pen className={`h-3 w-3 ${
                                    isActive 
                                      ? 'text-blue-600' 
                                      : signedFields.has(field.id)
                                      ? 'text-green-600'
                                      : hasAssignment 
                                        ? 'text-green-600' 
                                        : 'text-red-600'
                                  }`} />
                                </div>
                                
                                {/* Content on the right - takes remaining space */}
                                <div className="flex-1 text-center min-w-0">
                                  <div className="font-medium text-xs overflow-hidden text-ellipsis whitespace-nowrap">
                                    {signedFields.has(field.id) 
                                      ? '✓ Signed' 
                                      : hasAssignment 
                                        ? field.assignedTo.name 
                                        : 'Click to assign'
                                    }
                                  </div>
                                </div>
                              </div>
                            )}
            
                          </div>
                          
                          {/* Delete button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteField(field.id);
                            }}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 z-40 transition-colors"
                            style={{ transform: `scale(${1 / scale})` }}
                          >
                            <X size={12} />
                          </button>
                          
                          {/* Move indicator - always visible but more prominent when active */}
                          <div 
                            className={`absolute rounded-full p-1 shadow-sm pointer-events-none z-40 transition-all duration-200 ${
                              isActive && !isResizing 
                                ? 'bg-blue-500 text-white' 
                                : 'bg-gray-400 text-white opacity-60 hover:opacity-100'
                            }`}
                            style={{
                              top: '-12px',
                              left: '-12px',
                              transform: `scale(${1 / scale})`,
                              transformOrigin: 'center'
                            }}
                          >
                            <Move size={14} />
                          </div>
                          
                          {/* Resize handle - always visible but more prominent when active */}
                          <div 
                            className={`absolute rounded-tl-md cursor-nw-resize z-40 transition-all duration-200 ${
                              isActive && !isDragging
                                ? 'bg-blue-500 text-white hover:bg-blue-600' 
                                : 'bg-gray-400 text-white opacity-60 hover:opacity-100 hover:bg-gray-500'
                            }`}
                            style={{
                              bottom: '-2px',
                              right: '-2px',
                              width: `${12 / scale}px`,
                              height: `${12 / scale}px`,
                              transform: `scale(${1 / scale})`,
                              transformOrigin: 'bottom right'
                            }}
                            onMouseDown={(e) => handleResizeMouseDown(e, field.id)}
                          >
                            <Maximize2 size={8} className="absolute top-0.5 left-0.5" />
                          </div>
                          
                          {/* Signature ID bar - always visible for signature fields */}
                          <div className="absolute top-full left-0 w-full mt-1">
                            <div className={`text-xs font-medium truncate px-2 py-1 rounded ${
                              isActive 
                                ? 'text-blue-700 bg-blue-100'
                                : signedFields.has(field.id)
                                ? 'text-green-700 bg-green-200'
                                : hasAssignment
                                ? 'text-green-700 bg-green-100'
                                : 'text-red-700 bg-red-100'
                            }`}
                                 style={{ fontSize: '10px' }}>
                              SIGNED: {field.signatureId ? field.signatureId.substring(0, 23).toUpperCase() : 'PENDING'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  
                  {/* Temporary form field preview during placement */}
                  {/* {tempField && tempField.page === pageNumber && currentFieldType === 'form' && FORM_FIELD_TYPES[tempField.type] && (() => {
                    const fieldConfig = FORM_FIELD_TYPES[tempField.type] || FORM_FIELD_TYPES.text;
                    const FieldIcon = fieldConfig.icon;
                    return (
                      <div
                        className="absolute border-2 border-dashed border-green-500 bg-green-100/60 rounded-lg z-40 pointer-events-none"
                        style={{
                          left: `${tempField.x * scale}px`,
                          top: `${tempField.y * scale}px`,
                          width: `${tempField.width * scale}px`,
                          height: `${tempField.height * scale}px`
                        }}
                      >
                        <div className="flex items-center justify-center h-full text-green-700 text-xs font-medium">
                          <FieldIcon className="h-3 w-3 mr-1" />
                          {fieldConfig.label} Preview
                        </div>
                      </div>
                    );
                  })()} */}

                  {/* Form fields overlay */}
                  {formFields
                    .filter(field => field.page === pageNumber)
                    .map((field) => {
                      const isActive = field.id === activeField;
                      const hasAssignment = field.assignedTo;
                      const fieldConfig = FORM_FIELD_TYPES[field.type] || FORM_FIELD_TYPES.text;
                      const FieldIcon = fieldConfig.icon;
                      
                      return (
                        <div
                          key={field.id}
                          data-field-overlay="true"
                          className={`absolute border-2 select-none user-select-none pointer-events-auto rounded-lg
                            ${isActive 
                              ? "border-blue-500 bg-blue-100/40 shadow-lg z-40 cursor-move" 
                              : hasAssignment
                              ? "border-green-500 bg-green-100/40 hover:bg-green-100/60 z-20 cursor-move hover:cursor-move"
                              : "border-red-500 bg-red-100/40 hover:bg-red-100/60 z-20 cursor-move hover:cursor-move"
                            }
                            ${(isDragging || isResizing) && isActive ? 'transition-none' : 'transition-all duration-200 hover:scale-105'}
                          `}
                          style={{
                            left: `${field.x * scale}px`,
                            top: `${field.y * scale}px`,
                            width: `${(field.width || 150) * scale}px`,
                            height: `${(field.height || 50) * scale}px`,
                            willChange: (isDragging || isResizing) && isActive ? 'transform' : 'auto'
                          }}
                          onMouseDown={(e) => handleFieldMouseDown(e, field.id)}
                          onClick={(e) => handleFieldClick(field, e)}
                          onMouseEnter={() => setHoveredField(field.id)}
                          onMouseLeave={() => setHoveredField(null)}
                        >
                          <div className="flex flex-col h-full p-2 text-xs pointer-events-none">
                            {/* Progressive layout based on field dimensions - IDENTICAL to signature fields */}
                            {(field.height || 50) >= 70 ? (
                              // Tall field: Show icon + label at top, content below
                              <>
                                {/* Top section with field icon and label */}
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center space-x-1">
                                    <FieldIcon className={`h-3 w-3 ${
                                      isActive 
                                        ? 'text-blue-600' 
                                        : hasAssignment 
                                          ? 'text-green-600' 
                                          : 'text-red-600'
                                    }`} />
                                    <span className={`font-medium text-xs ${
                                      isActive 
                                        ? 'text-blue-700' 
                                        : hasAssignment 
                                          ? 'text-green-700' 
                                          : 'text-red-700'
                                    }`}>
                                      {fieldConfig.label}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Center section with contact assignment */}
                                <div className="flex-1 flex items-center justify-center">
                                  <div className="text-center w-full max-w-full">
                                    <div className="font-medium text-xs w-full overflow-hidden text-ellipsis whitespace-nowrap">
                                      {hasAssignment 
                                        ? field.assignedTo.name 
                                        : 'Click to assign'
                                      }
                                    </div>
                                    {hasAssignment && (field.height || 50) >= 50 && (
                                      <div className="text-gray-600 text-xs w-full overflow-hidden text-ellipsis whitespace-nowrap">{field.assignedTo.email}</div>
                                    )}
                                  </div>
                                </div>
                              </>
                            ) : (field.height || 50) >= 50 ? (
                              // Medium height field: Show icon + content centered with email but no field label
                              <div className="flex-1 flex items-center justify-center">
                                <div className="flex items-center space-x-2 w-full min-w-0">
                                  <FieldIcon className={`h-3 w-3 flex-shrink-0 ${
                                    isActive 
                                      ? 'text-blue-600' 
                                      : hasAssignment 
                                        ? 'text-green-600' 
                                        : 'text-red-600'
                                  }`} />
                                  <div className="flex-1 min-w-0 text-center">
                                    <div className="font-medium text-xs truncate">
                                      {hasAssignment 
                                        ? field.assignedTo.name 
                                        : 'Click to assign'
                                      }
                                    </div>
                                    {hasAssignment && (
                                      <div className="text-gray-600 text-xs truncate">{field.assignedTo.email}</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              // Short field: Show icon and content on same line - IDENTICAL to signature fields
                              <div className="flex-1 flex items-center w-full min-w-0">
                                {/* Icon on the left */}
                                <div className="flex-shrink-0 mr-2">
                                  <FieldIcon className={`h-3 w-3 ${
                                    isActive 
                                      ? 'text-blue-600' 
                                      : hasAssignment 
                                        ? 'text-green-600' 
                                        : 'text-red-600'
                                  }`} />
                                </div>
                                
                                {/* Content on the right - takes remaining space */}
                                <div className="flex-1 text-center min-w-0">
                                  <div className="font-medium text-xs overflow-hidden text-ellipsis whitespace-nowrap">
                                    {hasAssignment 
                                      ? field.assignedTo.name 
                                      : 'Click to assign'
                                    }
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Delete button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteField(field.id);
                            }}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 z-40 transition-colors"
                            style={{ transform: `scale(${1 / scale})` }}
                          >
                            <X size={12} />
                          </button>
                          
                          {/* Move indicator - always visible but more prominent when active */}
                          <div 
                            className={`absolute rounded-full p-1 shadow-sm pointer-events-none z-40 transition-all duration-200 ${
                              isActive && !isResizing 
                                ? 'bg-green-500 text-white' 
                                : 'bg-gray-400 text-white opacity-60 hover:opacity-100'
                            }`}
                            style={{
                              top: '-12px',
                              left: '-12px',
                              transform: `scale(${1 / scale})`,
                              transformOrigin: 'center'
                            }}
                          >
                            <Move size={14} />
                          </div>
                          
                          {/* Resize handle - always visible but more prominent when active */}
                          <div 
                            className={`absolute rounded-tl-md cursor-nw-resize z-40 transition-all duration-200 ${
                              isActive && !isDragging
                                ? 'bg-green-500 text-white hover:bg-green-600' 
                                : 'bg-gray-400 text-white opacity-60 hover:opacity-100 hover:bg-gray-500'
                            }`}
                            style={{
                              bottom: '-2px',
                              right: '-2px',
                              width: `${12 / scale}px`,
                              height: `${12 / scale}px`,
                              transform: `scale(${1 / scale})`,
                              transformOrigin: 'bottom right'
                            }}
                            onMouseDown={(e) => handleResizeMouseDown(e, field.id)}
                          >
                            <Maximize2 size={8} className="absolute top-0.5 left-0.5" />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </Document>
            ) : (
              <div className="flex justify-center items-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            )}
          </div>
        </div>
      
      {/* Footer with actions - matching DocumentViewer style */}
      <div className="bg-white border-t border-gray-200 p-4 flex justify-between">
        <div className="flex items-center">
          <span className="text-sm text-gray-500">
            {signatureFields.length} signature {signatureFields.length === 1 ? 'field' : 'fields'} placed
            {signatureFields.length > 0 && (
              <span className="ml-2">
                ({signatureFields.filter(f => f.assignedTo).length} assigned)
              </span>
            )}
            {formFields.length > 0 && (
              <span className="ml-4">
                | {formFields.length} form {formFields.length === 1 ? 'field' : 'fields'} placed
                {formFields.filter(f => f.assignedTo).length > 0 && (
                  <span className="ml-2">
                    ({formFields.filter(f => f.assignedTo).length} assigned)
                  </span>
                )}
              </span>
            )}
          </span>
          {activeField && (
            <div className="text-sm text-blue-600 ml-4">
              Selected field position: ({Math.round(((signatureFields.find(f => f.id === activeField) || formFields.find(f => f.id === activeField))?.x || 0) * 100) / 100}, {Math.round(((signatureFields.find(f => f.id === activeField) || formFields.find(f => f.id === activeField))?.y || 0) * 100) / 100})
            </div>
          )}
          {collisionWarning && (
            <div className="text-sm text-orange-600 ml-4 flex items-center">
              <AlertCircle size={16} className="mr-1" />
              Movement restricted by nearby fields
            </div>
          )}
        </div>
        <div className="ml-auto flex gap-4">
          {!embedded && (
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors border"
              style={{ backgroundColor: 'white', borderColor: 'rgba(148,163,184,0.2)' }}
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleComplete}
            disabled={(signatureFields.length === 0 && formFields.length === 0) || loading || signatureFields.some(f => !f.assignedTo)}
            className="relative inline-flex items-center px-6 py-2.5 rounded-xl text-white font-medium transition-all duration-300 disabled:cursor-not-allowed group"
            style={{
              background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
              boxShadow: '0 8px 25px rgba(30, 64, 175, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
              opacity: (signatureFields.length === 0 && formFields.length === 0) || loading || signatureFields.some(f => !f.assignedTo) ? 0.6 : 1
            }}
          >
            <span
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%)',
                animation: 'shimmer 2s infinite',
                borderRadius: '12px'
              }}
            />
            {loading ? (
              <RefreshCw className="animate-spin mr-2 relative z-10" size={18} />
            ) : (
              <Check size={18} className="mr-2 relative z-10" />
            )}
            <span className="relative z-10">{signatureFields.length > 0 ? 'Send for Signature' : 'Complete Document'}</span>
          </button>
        </div>
      </div>

      {/* Contact Assignment Modal */}
      {showAssignmentModal && selectedFieldForAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className="w-full max-w-lg mx-4 rounded-3xl border shadow-xl p-6 sm:p-7"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(249, 250, 251, 0.95))',
              borderColor: 'rgba(148, 163, 184, 0.2)'
            }}
          >
            <div className="flex items-center justify-between mb-4 pb-2 border-b" style={{ borderColor: 'rgba(148,163,184,0.15)' }}>
              <h3 className="text-xl font-semibold tracking-tight text-gray-900">
                {selectedFieldForAssignment?.type === 'contact-assignment' 
                  ? `Assign Contact Slot ${selectedFieldForAssignment.contactSlot}`
                  : `Assign ${selectedFieldForAssignment?.type === 'signature' ? 'Signature' : 'Form'} Field`
                }
              </h3>
              <button
                onClick={() => setShowAssignmentModal(false)}
                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            
            <p className="text-gray-600 mb-4">
              {selectedFieldForAssignment?.type === 'contact-assignment' 
                ? `Who should be assigned to keyboard shortcut "${selectedFieldForAssignment.contactSlot}"?`
                : `Who should ${selectedFieldForAssignment?.type === 'signature' ? 'sign' : 'fill out'} this field: "${selectedFieldForAssignment.label || FORM_FIELD_TYPES[selectedFieldForAssignment?.type]?.label}"?`
              }
            </p>
            
            {/* Current user option - only show for field assignment, not contact slot assignment */}
            {selectedFieldForAssignment?.type !== 'contact-assignment' && (
              <div className="mb-4">
                <button
                  onClick={() => assignFieldToContact({
                    id: 'current-user',
                    name: user?.name || 'Me',
                    email: user?.email || '',
                    isCurrentUser: true
                  })}
                  className="w-full p-3 border rounded-xl flex items-center text-left transition-all bg-white hover:bg-gray-50"
                  style={{ borderColor: 'rgba(148, 163, 184, 0.2)' }}
                >
                  <div className="mr-3 p-2 rounded-lg" style={{ background: 'rgba(30, 64, 175, 0.08)' }}>
                    <User size={16} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{user?.name || 'Me'}</div>
                    <div className="text-sm text-gray-600">{user?.email || ''}</div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded border" style={{ background: 'rgba(30,64,175,0.08)', color: '#1e40af', borderColor: 'rgba(148,163,184,0.2)' }}>Me</span>
                </button>
              </div>
            )}
            
            {/* Recently used contacts for this document */}
            {contacts.filter(c => !c.isCurrentUser).length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recently assigned in this document:
                </label>
                <div className="space-y-2">
                  {contacts.filter(c => !c.isCurrentUser).map((contact) => (
                    <button
                      key={contact.id}
                      onClick={() => assignFieldToContact(contact)}
                      className="w-full p-3 border rounded-xl flex items-center text-left transition-all bg-white hover:bg-gray-50"
                      style={{ borderColor: 'rgba(148, 163, 184, 0.2)' }}
                    >
                      <div className="mr-3 p-2 rounded-lg" style={{ background: 'rgba(34, 197, 94, 0.08)' }}>
                        <User size={16} className="text-green-600" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{contact.name}</div>
                        <div className="text-sm text-gray-600">{contact.email}</div>
                      </div>
                      <span className="text-xs px-2 py-1 rounded border" style={{ background: 'rgba(34,197,94,0.08)', color: '#15803d', borderColor: 'rgba(148,163,184,0.2)' }}>Recent</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {selectedFieldForAssignment?.type === 'contact-assignment' 
                  ? 'Select from contacts:'
                  : 'Or select from your contacts:'
                }
              </label>
              <ContactSelector
                onSelectContact={(contact) => {
                  // Always assign immediately, adding to local contacts if needed
                  const contactObj = {
                    id: contact.id || `contact-${Date.now()}`,
                    name: contact.name,
                    email: contact.email,
                    isCurrentUser: false
                  };
                  assignFieldToContact(contactObj);
                }}
                placeholder="Search your contacts or enter email..."
                className="w-full"
              />
            </div>
            
            {/* Manual entry */}
            {!showNewContactForm ? (
              <button
                onClick={() => setShowNewContactForm(true)}
                className="w-full p-3 border-2 border-dashed rounded-xl flex items-center justify-center text-gray-700 hover:text-gray-900 transition-colors bg-white"
                style={{ borderColor: 'rgba(148,163,184,0.3)' }}
              >
                <UserPlus size={20} className="mr-2" />
                Add New Contact Manually
              </button>
            ) : (
              <div className="border rounded-xl p-4 bg-white"
                   style={{ borderColor: 'rgba(148,163,184,0.2)' }}>
                <div className="space-y-3">
                  {/* Error message display */}
                  {contactCreationError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-red-700 text-sm">{contactCreationError}</p>
                    </div>
                  )}
                  
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={newContactName}
                    onChange={(e) => {
                      setNewContactName(e.target.value);
                      if (contactCreationError) setContactCreationError('');
                    }}
                    disabled={creatingContact}
                    className="w-full px-3 py-2 bg-white border rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1e40af] focus:border-[#1e40af] disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderColor: 'rgba(148,163,184,0.3)' }}
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={newContactEmail}
                    onChange={(e) => {
                      setNewContactEmail(e.target.value);
                      if (contactCreationError) setContactCreationError('');
                    }}
                    disabled={creatingContact}
                    className="w-full px-3 py-2 bg-white border rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1e40af] focus:border-[#1e40af] disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderColor: 'rgba(148,163,184,0.3)' }}
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={addNewContact}
                      disabled={creatingContact || !newContactName.trim() || !newContactEmail.trim()}
                      className="flex-1 px-3 py-2 text-white rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      style={{
                        background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                        boxShadow: '0 8px 25px rgba(30, 64, 175, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      {creatingContact ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                          Creating...
                        </>
                      ) : (
                        'Add Contact'
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowNewContactForm(false);
                        setNewContactName('');
                        setNewContactEmail('');
                        setContactCreationError('');
                      }}
                      disabled={creatingContact}
                      className="px-3 py-2 rounded-xl text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white border"
                      style={{ borderColor: 'rgba(148,163,184,0.3)' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Signature Modal - Commented out for later version */}
      {/* {showSignatureModal && fieldToSign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <Pen className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Sign Now?
                  </h3>
                  <p className="text-sm text-gray-600">
                    You can sign this field now or skip and sign later
                  </p>
                </div>
              </div>
              
              <div className="mb-6">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <h5 className="text-xs font-medium text-gray-700 mb-1">Field:</h5>
                  <p className="text-sm text-gray-900">Signature field on page {fieldToSign.page}</p>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleSkipSigning}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Skip for Now
                </button>
                <button
                  onClick={() => handleSignField({ type: 'placeholder', data: 'User signature' })}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                >
                  <Edit3 size={16} className="mr-2" />
                  Sign Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )} */}
      
      {/* Signature placement overlay */}
      {showSignaturePlacement && selectedSignatureField && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
          <div className="container mx-auto p-4 h-full">
            <SignaturePlacement
              documentData={{
                url: fileUrl,
                name: documentData?.name || 'Document',
                pageCount: numPages
              }}
              onSignatureAdded={handleSignatureAddedInternal}
              onCancel={() => {
                setShowSignaturePlacement(false);
                setSelectedSignatureField(null);
              }}
              documentId={documentId}
              user={user}
              API_URL={API_URL}
              signatureFields={signatureFields}
              selectedField={selectedSignatureField}
              isOwner={true}
            />
          </div>
        </div>
      )}

      {/* Floating Pagination Mini-Bar - Bottom Center */}
      {numPages && numPages > 1 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-40">
          <div 
            className="bg-white rounded-full shadow-lg border px-4 py-2 flex items-center space-x-3"
            style={{
              fontFamily: fontStyles.fontFamily,
              borderColor: colors.gray200,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
              backdropFilter: 'blur(12px)',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9))'
            }}
          >
            <button
              onClick={previousPage}
              disabled={pageNumber <= 1}
              className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-600 transition-all duration-200 disabled:cursor-not-allowed"
              style={{
                color: pageNumber <= 1 ? colors.gray300 : colors.gray700
              }}
              title="Previous page"
            >
              <ChevronLeft size={20} />
            </button>
            
            <div 
              className="px-4 py-1 text-sm font-medium select-none"
              style={{
                color: colors.gray900,
                ...fontStyles.medium,
                letterSpacing: '-0.01em',
                minWidth: '100px',
                textAlign: 'center'
              }}
            >
              Page {pageNumber} of {numPages}
            </div>
            
            <button
              onClick={nextPage}
              disabled={pageNumber >= numPages}
              className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-600 transition-all duration-200 disabled:cursor-not-allowed"
              style={{
                color: pageNumber >= numPages ? colors.gray300 : colors.gray700
              }}
              title="Next page"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Floating Zoom Controls Mini-Toolbar - Bottom Right */}
      <div className="fixed bottom-6 right-6 z-40 flex items-center space-x-3">
        {/* Zoom Controls */}
        <div 
          className="bg-white rounded-full shadow-lg border px-3 py-2 flex items-center space-x-2"
          style={{
            fontFamily: fontStyles.fontFamily,
            borderColor: colors.gray200,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
            backdropFilter: 'blur(12px)',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9))'
          }}
        >
          <button 
            onClick={zoomOut}
            disabled={scale <= 0.5}
            className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-600 transition-all duration-200 disabled:cursor-not-allowed"
            style={{
              color: scale <= 0.5 ? colors.gray300 : colors.gray700
            }}
            title="Zoom out"
          >
            <ZoomOut size={18} />
          </button>
          
          <div 
            className="px-3 py-1 text-sm font-medium select-none"
            style={{
              color: colors.gray900,
              ...fontStyles.medium,
              letterSpacing: '-0.01em',
              minWidth: '65px',
              textAlign: 'center'
            }}
          >
            {Math.round(scale * 100)}%
          </div>
          
          <button 
            onClick={zoomIn}
            disabled={scale >= 3}
            className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50 text-gray-600 transition-all duration-200 disabled:cursor-not-allowed"
            style={{
              color: scale >= 3 ? colors.gray300 : colors.gray700
            }}
            title="Zoom in"
          >
            <ZoomIn size={18} />
          </button>
        </div>
      </div>
      
    </div>
  );
};

export default SignatureFieldPlacement;